import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, PieChart, Pie, Legend } from 'recharts';
import type { Flow, Card, Departamento, Colaborador } from '../../types';
import { BarChart2, Clock, LayoutGrid, Maximize2, X, AlertTriangle, CheckCircle2, TrendingUp, Users, Table2, Building2, ChevronDown, ChevronUp, Filter, Calendar, Save, Bookmark, Trash2, MoreVertical, Pencil, Layers, Presentation } from 'lucide-react';
import { getApiBase } from '../../utils/apiBase';
import { calcularHorasSLA } from '../../utils/slaUtils';
import { useToast } from '../../contexts/ToastContext';
import Segmentacao from './Segmentacao';
import ApresentacaoFluxo from './ApresentacaoFluxo';

interface DashboardProps {
  flows: Flow[];
  cards: Card[];
  departamentos?: Departamento[];
  colaboradores?: Colaborador[];
}

// Cores modernas para os gráficos
const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#14B8A6', '#EC4899', '#6366F1'];

// Status SLA de um card
type SLAStatus = 'noPrazo' | 'atencao' | 'atrasado' | 'semSLA';

interface CardSLAInfo {
  card: Card;
  etapaId: string;
  etapaNome: string;
  responsavelId?: string;
  responsavelNome?: string;
  departamentoId?: string;
  departamentoNome?: string;
  horasNaEtapa: number;
  tempoMaximoHoras?: number;
  percentualSLA: number;
  status: SLAStatus;
}

// Visão salva do dashboard
interface DashboardView {
  _id: string;
  nome: string;
  flowId: string;
  departamentoId: string;
  viewMode: 'graficos' | 'tabela' | 'colaboradores' | 'segmentacao' | 'apresentacao';
  tipoFiltroData: 'criacao' | 'movimentacao';
  dataInicio: string;
  dataFim: string;
  etapasSLA: string[];
  periodoRelativo?: string;
}

function getSession(): { empresaId: string; usuarioId: string } | null {
  try {
    const raw = localStorage.getItem('session');
    if (!raw) return null;
    const s = JSON.parse(raw);
    const empresaId = s?.empresa?.id || s?.usuario?.empresaId;
    const usuarioId = s?.usuario?.id || s?.usuario?._id;
    if (!empresaId || !usuarioId) return null;
    return { empresaId, usuarioId };
  } catch { return null; }
}

const Dashboard: React.FC<DashboardProps> = ({ flows, cards, departamentos = [], colaboradores = [] }) => {
  const { success: toastSuccess, error: toastError } = useToast();
  const [selectedFlowId, setSelectedFlowId] = useState<string>('');
  const [selectedDepartamentoId, setSelectedDepartamentoId] = useState<string>('');
  const [viewMode, setViewMode] = useState<'graficos' | 'tabela' | 'colaboradores' | 'segmentacao' | 'apresentacao'>('graficos');
  const [fullscreenMode, setFullscreenMode] = useState<'tempo' | 'distribuicao' | 'tabela' | 'sla' | 'colaboradores' | null>(null);
  const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' }>({ key: 'tempoMedioDias', direction: 'desc' });
  const [colaboradorSortConfig, setColaboradorSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' }>({ key: 'atrasados', direction: 'desc' });

  // Filtro de etapas para SLA
  const [etapasSLA, setEtapasSLA] = useState<Set<string>>(new Set());
  const [mostrarFiltroEtapas, setMostrarFiltroEtapas] = useState(false);
  const filtroEtapasRef = useRef<HTMLDivElement>(null);

  // Filtro de data
  const [dataInicio, setDataInicio] = useState<string>('');
  const [dataFim, setDataFim] = useState<string>('');
  const [tipoFiltroData, setTipoFiltroData] = useState<'criacao' | 'movimentacao'>('movimentacao');

  // Visões salvas
  const [savedViews, setSavedViews] = useState<DashboardView[]>([]);
  const [activeViewId, setActiveViewId] = useState<string | null>(null);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [newViewName, setNewViewName] = useState('');
  const saveInputRef = useRef<HTMLInputElement>(null);
  // CRUD das visões: menu de ações e renomeio inline
  const [menuViewId, setMenuViewId] = useState<string | null>(null);
  const [editingViewId, setEditingViewId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');
  const menuVisaoRef = useRef<HTMLDivElement>(null);
  const renameInputRef = useRef<HTMLInputElement>(null);

  // Carregar visões do Atlas ao montar
  const fetchSavedViews = useCallback(async () => {
    const sess = getSession();
    if (!sess) return;
    try {
      const res = await fetch(`${getApiBase()}/dashboard-views?empresaId=${encodeURIComponent(sess.empresaId)}&usuarioId=${encodeURIComponent(sess.usuarioId)}`);
      if (res.ok) {
        const data = await res.json();
        setSavedViews(data);
      }
    } catch (err) {
      console.error('Erro ao carregar visões salvas:', err);
    }
  }, []);

  useEffect(() => {
    fetchSavedViews();
  }, [fetchSavedViews]);

  // ===== Escalonamentos de SLA (card que dobrou/triplicou o prazo da etapa) =====
  // Vem do backend porque é histórico de aviso enviado, não dá pra recalcular
  // olhando o estado atual do card: quem foi avisado, quando e em que nível só
  // existe gravado.
  const [escalonamentos, setEscalonamentos] = useState<any[]>([]);

  useEffect(() => {
    const sess = getSession();
    if (!sess) return;
    const params = new URLSearchParams({ empresaId: sess.empresaId });
    if (selectedFlowId) params.set('flowId', selectedFlowId);

    fetch(`${getApiBase()}/sla/escalonamentos?${params.toString()}`)
      .then(r => (r.ok ? r.json() : []))
      .then(d => setEscalonamentos(Array.isArray(d) ? d : []))
      .catch(() => setEscalonamentos([]));
  }, [selectedFlowId]);

  const escalonamentoStats = useMemo(() => {
    // Quantos cards chegaram a cada múltiplo do prazo
    const porNivel = new Map<number, Set<string>>();
    // Por pessoa: quantos avisos recebeu e em quantos deles o card seguiu parado
    const porPessoa = new Map<string, {
      nome: string;
      avisos: number;
      cards: Set<string>;
      semAcao: number;      // foi avisado e o card escalou de novo depois
      nivelMaximo: number;
    }>();

    // Maior nível que cada passagem de card atingiu — é o que diz se um aviso
    // virou ação: se depois dele o card escalou mais, não virou.
    const nivelMaximoPorCard = new Map<string, number>();
    escalonamentos.forEach(e => {
      const chave = `${e.cardId}|${e.entradaEtapa}`;
      nivelMaximoPorCard.set(chave, Math.max(nivelMaximoPorCard.get(chave) || 0, e.nivel || 0));
    });

    escalonamentos.forEach(e => {
      const nivel = e.nivel || 0;
      if (!porNivel.has(nivel)) porNivel.set(nivel, new Set());
      porNivel.get(nivel)!.add(e.cardId);

      const chave = `${e.cardId}|${e.entradaEtapa}`;
      const escalouDepois = (nivelMaximoPorCard.get(chave) || 0) > nivel;

      (e.destinatarios || []).forEach((d: any) => {
        const id = d.colaboradorId || d.email;
        if (!id) return;
        if (!porPessoa.has(id)) {
          porPessoa.set(id, { nome: d.nome || d.email, avisos: 0, cards: new Set(), semAcao: 0, nivelMaximo: 0 });
        }
        const p = porPessoa.get(id)!;
        p.avisos += 1;
        p.cards.add(e.cardId);
        p.nivelMaximo = Math.max(p.nivelMaximo, nivel);
        if (escalouDepois) p.semAcao += 1;
      });
    });

    const niveis = Array.from(porNivel.entries())
      .map(([nivel, cards]) => ({ nivel, cards: cards.size }))
      .sort((a, b) => a.nivel - b.nivel);

    const pessoas = Array.from(porPessoa.values())
      .map(p => ({ ...p, cards: p.cards.size }))
      .sort((a, b) => b.semAcao - a.semAcao || b.avisos - a.avisos);

    return {
      niveis,
      pessoas,
      totalAvisos: escalonamentos.length,
      cardsAfetados: new Set(escalonamentos.map(e => e.cardId)).size,
      semDestinatario: escalonamentos.filter(e => e.semDestinatario).length,
    };
  }, [escalonamentos]);

  // Detectar período relativo atual
  const detectPeriodoRelativo = (): string | undefined => {
    if (!dataInicio && !dataFim) return undefined;
    const hoje = new Date().toISOString().split('T')[0];
    if (dataInicio === hoje && dataFim === hoje) return 'hoje';
    const d7 = new Date(); d7.setDate(d7.getDate() - 7);
    if (dataInicio === d7.toISOString().split('T')[0] && dataFim === hoje) return '7dias';
    const d30 = new Date(); d30.setDate(d30.getDate() - 30);
    if (dataInicio === d30.toISOString().split('T')[0] && dataFim === hoje) return '30dias';
    const inicioMes = new Date(); inicioMes.setDate(1);
    if (dataInicio === inicioMes.toISOString().split('T')[0] && dataFim === hoje) return 'esteMes';
    return undefined;
  };

  // Aplicar período relativo (recalcula datas)
  const aplicarPeriodoRelativo = (periodo: string | undefined) => {
    if (!periodo) return;
    const hoje = new Date();
    switch (periodo) {
      case 'hoje': {
        const h = hoje.toISOString().split('T')[0];
        setDataInicio(h); setDataFim(h); break;
      }
      case '7dias': {
        const ini = new Date(); ini.setDate(ini.getDate() - 7);
        setDataInicio(ini.toISOString().split('T')[0]); setDataFim(hoje.toISOString().split('T')[0]); break;
      }
      case '30dias': {
        const ini = new Date(); ini.setDate(ini.getDate() - 30);
        setDataInicio(ini.toISOString().split('T')[0]); setDataFim(hoje.toISOString().split('T')[0]); break;
      }
      case 'esteMes': {
        const ini = new Date(hoje.getFullYear(), hoje.getMonth(), 1);
        setDataInicio(ini.toISOString().split('T')[0]); setDataFim(hoje.toISOString().split('T')[0]); break;
      }
    }
  };

  // Snapshot dos filtros atuais — usado ao criar e ao atualizar uma visão
  const filtrosAtuais = () => ({
    flowId: selectedFlowId,
    departamentoId: selectedDepartamentoId,
    viewMode,
    tipoFiltroData,
    dataInicio,
    dataFim,
    etapasSLA: Array.from(etapasSLA),
    periodoRelativo: detectPeriodoRelativo() || '',
  });

  /** true se os filtros na tela divergem dos que estão salvos na visão. */
  const visaoAlterada = (view: DashboardView): boolean => {
    if (activeViewId !== view._id) return false;
    const atual = filtrosAtuais();
    const etapasSalvas = Array.isArray(view.etapasSLA) ? view.etapasSLA : [];
    if (
      atual.flowId !== view.flowId ||
      atual.departamentoId !== (view.departamentoId || '') ||
      atual.viewMode !== view.viewMode ||
      atual.tipoFiltroData !== view.tipoFiltroData ||
      atual.periodoRelativo !== (view.periodoRelativo || '')
    ) return true;
    // Com período relativo as datas são recalculadas a cada carga — não comparar
    if (!atual.periodoRelativo && (atual.dataInicio !== (view.dataInicio || '') || atual.dataFim !== (view.dataFim || ''))) return true;
    if (atual.etapasSLA.length !== etapasSalvas.length) return true;
    const salvas = new Set(etapasSalvas);
    return atual.etapasSLA.some(id => !salvas.has(id));
  };

  const salvarVisao = async () => {
    if (!newViewName.trim() || !selectedFlowId) {
      toastError(!selectedFlowId ? 'Selecione um fluxo antes de salvar a visão' : 'Digite um nome para a visão');
      return;
    }
    const sess = getSession();
    if (!sess) {
      toastError('Sessão inválida. Faça login novamente.');
      return;
    }
    try {
      const res = await fetch(`${getApiBase()}/dashboard-views`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          nome: newViewName.trim(),
          empresaId: sess.empresaId,
          usuarioId: sess.usuarioId,
          ...filtrosAtuais(),
        })
      });
      if (res.ok) {
        const saved = await res.json();
        setSavedViews(prev => [...prev, saved]);
        setActiveViewId(saved._id);
        toastSuccess(`Visão "${saved.nome}" salva com sucesso`);
      } else {
        const txt = await res.text().catch(() => '');
        console.error('Falha ao salvar visão:', res.status, txt);
        toastError(`Erro ao salvar visão (HTTP ${res.status})`);
      }
    } catch (err) {
      console.error('Erro ao salvar visão:', err);
      toastError('Erro de rede ao salvar visão');
    }
    setShowSaveDialog(false);
    setNewViewName('');
  };

  const carregarVisao = (view: DashboardView) => {
    loadingViewRef.current = true;
    setActiveViewId(view._id);
    setSelectedFlowId(view.flowId);
    setSelectedDepartamentoId(view.departamentoId);
    setViewMode(view.viewMode);
    setTipoFiltroData(view.tipoFiltroData);
    setEtapasSLA(new Set(Array.isArray(view.etapasSLA) ? view.etapasSLA : []));
    if (view.periodoRelativo) {
      aplicarPeriodoRelativo(view.periodoRelativo);
    } else {
      setDataInicio(view.dataInicio);
      setDataFim(view.dataFim);
    }
  };

  /** PUT genérico numa visão salva. Retorna a visão atualizada ou null. */
  const editarVisao = async (id: string, campos: Record<string, any>): Promise<DashboardView | null> => {
    const sess = getSession();
    if (!sess) {
      toastError('Sessão inválida. Faça login novamente.');
      return null;
    }
    try {
      const res = await fetch(`${getApiBase()}/dashboard-views/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ empresaId: sess.empresaId, usuarioId: sess.usuarioId, ...campos }),
      });
      if (!res.ok) {
        const txt = await res.text().catch(() => '');
        console.error('Falha ao editar visão:', res.status, txt);
        toastError(`Erro ao editar visão (HTTP ${res.status})`);
        return null;
      }
      const atualizada: DashboardView = await res.json();
      setSavedViews(prev => prev.map(v => (v._id === id ? atualizada : v)));
      return atualizada;
    } catch (err) {
      console.error('Erro ao editar visão:', err);
      toastError('Erro de rede ao editar visão');
      return null;
    }
  };

  /** Regrava a visão com os filtros que estão na tela agora. */
  const atualizarFiltrosVisao = async (view: DashboardView) => {
    if (!selectedFlowId) {
      toastError('Selecione um fluxo antes de atualizar a visão');
      return;
    }
    const atualizada = await editarVisao(view._id, filtrosAtuais());
    if (atualizada) {
      setActiveViewId(atualizada._id);
      setMenuViewId(null);
      toastSuccess(`Visão "${atualizada.nome}" atualizada com os filtros atuais`);
    }
  };

  const renomearVisao = async (view: DashboardView) => {
    const nome = editingName.trim();
    if (!nome) {
      toastError('O nome da visão não pode ficar vazio');
      return;
    }
    if (nome === view.nome) {
      setEditingViewId(null);
      return;
    }
    const atualizada = await editarVisao(view._id, { nome });
    if (atualizada) {
      setEditingViewId(null);
      setEditingName('');
      toastSuccess(`Visão renomeada para "${atualizada.nome}"`);
    }
  };

  const excluirVisao = async (id: string) => {
    const sess = getSession();
    if (!sess) return;
    const view = savedViews.find(v => v._id === id);
    if (!window.confirm(`Excluir a visão "${view?.nome || ''}"? Essa ação não pode ser desfeita.`)) return;
    try {
      const res = await fetch(`${getApiBase()}/dashboard-views/${id}?empresaId=${encodeURIComponent(sess.empresaId)}&usuarioId=${encodeURIComponent(sess.usuarioId)}`, {
        method: 'DELETE'
      });
      if (!res.ok) {
        toastError(`Erro ao excluir visão (HTTP ${res.status})`);
        return;
      }
      setSavedViews(prev => prev.filter(v => v._id !== id));
      if (activeViewId === id) setActiveViewId(null);
      setMenuViewId(null);
      toastSuccess('Visão excluída');
    } catch (err) {
      console.error('Erro ao excluir visão:', err);
      toastError('Erro de rede ao excluir visão');
    }
  };

  // Focar input ao abrir dialog
  useEffect(() => {
    if (showSaveDialog && saveInputRef.current) {
      saveInputRef.current.focus();
    }
  }, [showSaveDialog]);

  // Focar input ao começar a renomear
  useEffect(() => {
    if (editingViewId && renameInputRef.current) {
      renameInputRef.current.focus();
      renameInputRef.current.select();
    }
  }, [editingViewId]);

  // Fechar menu de ações da visão ao clicar fora
  useEffect(() => {
    if (!menuViewId) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (menuVisaoRef.current && !menuVisaoRef.current.contains(event.target as Node)) {
        setMenuViewId(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [menuViewId]);

  // Estado para drill-down
  const [drillDown, setDrillDown] = useState<{
    colaboradorId: string;
    colaboradorNome: string;
    tipo: 'noPrazo' | 'atencao' | 'atrasados' | 'passagens';
    etapaSelecionada?: string;
  } | null>(null);

  // Mapear colaboradores para lookup rápido
  const colaboradoresMap = useMemo(() => {
    const map = new Map<string, Colaborador>();
    colaboradores.forEach(col => {
      map.set(String(col.id), col);
      if ((col as any)._id) map.set(String((col as any)._id), col);
    });
    return map;
  }, [colaboradores]);

  // Mapear departamentos para lookup rápido
  const departamentosMap = useMemo(() => {
    const map = new Map<string, Departamento>();
    departamentos.forEach(dep => {
      map.set(String(dep.id), dep);
      if ((dep as any)._id) map.set(String((dep as any)._id), dep);
    });
    return map;
  }, [departamentos]);

  // Selecionar todas etapas ao trocar de fluxo (apenas se não está carregando visão salva)
  const loadingViewRef = useRef(false);
  useEffect(() => {
    if (loadingViewRef.current) {
      loadingViewRef.current = false;
      return;
    }
    if (selectedFlowId) {
      const selectedFlow = flows.find(f => f.id === selectedFlowId);
      const ids = selectedFlow?.etapas?.map(e => e.id) || [];
      setEtapasSLA(new Set(ids));
    } else {
      setEtapasSLA(new Set());
    }
    setMostrarFiltroEtapas(false);
    setDataInicio('');
    setDataFim('');
  }, [selectedFlowId, flows]);

  // Fechar dropdown de filtro ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (filtroEtapasRef.current && !filtroEtapasRef.current.contains(event.target as Node)) {
        setMostrarFiltroEtapas(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Calcular informações de SLA detalhadas de todos os cards
  // Quando filtro de data está ativo: análise HISTÓRICA (cards que passaram pela etapa no período)
  // Quando sem filtro de data: análise em tempo real (estado atual dos cards)
  const cardsSLAInfo = useMemo((): CardSLAInfo[] => {
    if (!selectedFlowId) return [];

    const selectedFlow = flows.find(f => f.id === selectedFlowId);
    if (!selectedFlow) return [];

    const etapaIdsDoFluxo = new Set(selectedFlow.etapas?.map(e => e.id) || []);
    const hasDateFilter = !!(dataInicio || dataFim);

    // Buscar cards do fluxo (incluindo cards que passaram pelo fluxo se filtro de data ativo)
    let flowCards: Card[];
    if (hasDateFilter) {
      flowCards = cards.filter(card => {
        if (etapaIdsDoFluxo.has(card.etapaId)) return true;
        return card.etapaHistory?.some(h => etapaIdsDoFluxo.has(h.etapaId)) || false;
      });
    } else {
      flowCards = cards.filter(card => {
        const cardFlow = flows.find(f => f.etapas?.some(e => e.id === card.etapaId));
        return cardFlow?.id === selectedFlowId;
      });
    }

    if (hasDateFilter) {
      // === ANÁLISE HISTÓRICA: cards que passaram pelas etapas no período ===
      const inicio = dataInicio ? new Date(dataInicio) : null;
      const fim = dataFim ? new Date(dataFim + 'T23:59:59') : null;
      const results: CardSLAInfo[] = [];

      flowCards.forEach(card => {
        // Filtro por data de criação: só inclui cards criados no período
        if (tipoFiltroData === 'criacao') {
          const dataCriacao = new Date(card.createdAt);
          if (inicio && dataCriacao < inicio) return;
          if (fim && dataCriacao > fim) return;
        }

        const history = [...(card.etapaHistory || [])].sort((a, b) =>
          new Date(a.movedAt).getTime() - new Date(b.movedAt).getTime()
        );

        history.forEach((entry, index) => {
          if (!etapaIdsDoFluxo.has(entry.etapaId)) return;

          const etapa = selectedFlow.etapas?.find(e => e.id === entry.etapaId);
          if (!etapa) return;

          const entradaTime = new Date(entry.movedAt).getTime();
          let saidaTime: number;

          if (entry.leftAt) {
            saidaTime = new Date(entry.leftAt).getTime();
          } else if (index < history.length - 1) {
            saidaTime = new Date(history[index + 1].movedAt).getTime();
          } else {
            saidaTime = new Date().getTime();
          }

          // Para filtro por movimentação: verificar se a passagem sobrepõe o período
          if (tipoFiltroData === 'movimentacao') {
            if (inicio && saidaTime < inicio.getTime()) return;
            if (fim && entradaTime > fim.getTime()) return;
          }

          const horasNaEtapa = calcularHorasSLA(new Date(entradaTime), new Date(saidaTime), etapa.slaIgnorarFimDeSemana);

          let status: SLAStatus = 'semSLA';
          let percentualSLA = 0;

          if (etapa.tempoMaximoHoras && horasNaEtapa > 0) {
            percentualSLA = (horasNaEtapa / etapa.tempoMaximoHoras) * 100;
            if (horasNaEtapa > etapa.tempoMaximoHoras) {
              status = 'atrasado';
            } else if (percentualSLA > 80) {
              status = 'atencao';
            } else {
              status = 'noPrazo';
            }
          }

          const responsavelId = entry.responsavelId || card.responsavelIndividualId;
          const responsavel = responsavelId ? colaboradoresMap.get(String(responsavelId)) : undefined;
          const departamentoId = responsavel?.departamentoId || etapa.departamentoId;
          const departamento = departamentoId ? departamentosMap.get(String(departamentoId)) : undefined;

          results.push({
            card,
            etapaId: etapa.id,
            etapaNome: etapa.nome || 'Desconhecida',
            responsavelId,
            responsavelNome: responsavel?.nome,
            departamentoId,
            departamentoNome: departamento?.nome,
            horasNaEtapa,
            tempoMaximoHoras: etapa.tempoMaximoHoras,
            percentualSLA,
            status
          });
        });
      });

      return results;
    } else {
      // === ANÁLISE EM TEMPO REAL: estado atual dos cards ===
      return flowCards.map(card => {
        const etapa = selectedFlow.etapas?.find(e => e.id === card.etapaId);

        const responsavelId = card.responsavelIndividualId;
        const responsavel = responsavelId ? colaboradoresMap.get(String(responsavelId)) : undefined;
        const departamentoId = responsavel?.departamentoId || etapa?.departamentoId;
        const departamento = departamentoId ? departamentosMap.get(String(departamentoId)) : undefined;

        const historicoEtapa = card.etapaHistory?.find(h => String(h.etapaId) === String(etapa?.id));
        let horasNaEtapa = 0;

        if (historicoEtapa) {
          const entradaNaEtapa = new Date(historicoEtapa.movedAt);
          if (!isNaN(entradaNaEtapa.getTime())) {
            horasNaEtapa = calcularHorasSLA(entradaNaEtapa, new Date(), etapa?.slaIgnorarFimDeSemana);
          }
        }

        let status: SLAStatus = 'semSLA';
        let percentualSLA = 0;

        if (etapa?.tempoMaximoHoras && horasNaEtapa > 0) {
          percentualSLA = (horasNaEtapa / etapa.tempoMaximoHoras) * 100;
          if (horasNaEtapa > etapa.tempoMaximoHoras) {
            status = 'atrasado';
          } else if (percentualSLA > 80) {
            status = 'atencao';
          } else {
            status = 'noPrazo';
          }
        }

        return {
          card,
          etapaId: card.etapaId,
          etapaNome: etapa?.nome || 'Desconhecida',
          responsavelId: responsavelId,
          responsavelNome: responsavel?.nome,
          departamentoId: departamentoId,
          departamentoNome: departamento?.nome,
          horasNaEtapa,
          tempoMaximoHoras: etapa?.tempoMaximoHoras,
          percentualSLA,
          status
        };
      });
    }
  }, [selectedFlowId, flows, cards, colaboradoresMap, departamentosMap, dataInicio, dataFim, tipoFiltroData]);

  // Filtrar por departamento e etapas selecionadas
  // (filtro de data já é aplicado em cardsSLAInfo)
  const filteredCardsSLAInfo = useMemo(() => {
    let filtered = cardsSLAInfo;
    // Filtrar por etapas selecionadas para SLA
    if (etapasSLA.size > 0) {
      filtered = filtered.filter(info => etapasSLA.has(info.etapaId));
    }
    // Filtrar por departamento
    if (selectedDepartamentoId) {
      filtered = filtered.filter(info => info.departamentoId === selectedDepartamentoId);
    }
    return filtered;
  }, [cardsSLAInfo, selectedDepartamentoId, etapasSLA]);

  // Calcular estatísticas de SLA a partir das informações filtradas
  const slaStats = useMemo(() => {
    const stats = { noPrazo: 0, atencao: 0, atrasados: 0, semSLA: 0, total: 0, totalPassagens: 0 };
    
    // Para cada card único, determinar o PIOR status SLA entre todas as suas passagens
    // Prioridade: atrasado > atencao > noPrazo > semSLA
    const cardPiorStatus = new Map<string, SLAStatus>();
    const prioridade: Record<SLAStatus, number> = { semSLA: 0, noPrazo: 1, atencao: 2, atrasado: 3 };
    
    filteredCardsSLAInfo.forEach(info => {
      const cardId = String(info.card.id || (info.card as any)._id);
      stats.totalPassagens++;
      const statusAtual = cardPiorStatus.get(cardId);
      if (!statusAtual || prioridade[info.status] > prioridade[statusAtual]) {
        cardPiorStatus.set(cardId, info.status);
      }
    });

    cardPiorStatus.forEach(status => {
      stats.total++;
      switch (status) {
        case 'noPrazo': stats.noPrazo++; break;
        case 'atencao': stats.atencao++; break;
        case 'atrasado': stats.atrasados++; break;
        case 'semSLA': stats.semSLA++; break;
      }
    });

    return stats;
  }, [filteredCardsSLAInfo]);

  // Dados do gráfico de pizza SLA
  const slaPieData = useMemo(() => {
    return [
      { name: 'No Prazo', value: slaStats.noPrazo, color: '#10B981' },
      { name: 'Atenção (>80%)', value: slaStats.atencao, color: '#F59E0B' },
      { name: 'Atrasados', value: slaStats.atrasados, color: '#EF4444' },
      { name: 'Sem SLA', value: slaStats.semSLA, color: '#9CA3AF' }
    ].filter(item => item.value > 0);
  }, [slaStats]);

  // Calcular reprocessos por etapa (cards que voltaram a uma etapa pela qual já passaram)
  // Para cada card, conta cada entrada em uma etapa após a primeira visita = 1 reprocesso.
  // Útil para identificar gargalos onde cards retornam (ex: Controladoria → Aguardando Nota Fiscal).
  const reprocessosPorEtapa = useMemo(() => {
    const result = new Map<string, { passagens: number; cards: Set<string>; vindoDe: Map<string, number> }>();
    if (!selectedFlowId) return result;

    const selectedFlow = flows.find(f => f.id === selectedFlowId);
    if (!selectedFlow) return result;

    const etapaIdsDoFluxo = new Set((selectedFlow.etapas || []).map(e => String(e.id)));
    const etapaNomeMap = new Map<string, string>();
    selectedFlow.etapas?.forEach(e => etapaNomeMap.set(String(e.id), e.nome || 'Desconhecida'));

    const hasDateFilter = !!(dataInicio || dataFim);
    const inicioMs = dataInicio ? new Date(dataInicio).getTime() : 0;
    const fimMs = dataFim ? new Date(dataFim + 'T23:59:59').getTime() : Infinity;

    // Mesma lógica de seleção de cards usada em cardsSLAInfo
    let flowCards: Card[];
    if (hasDateFilter) {
      flowCards = cards.filter(card => {
        if (etapaIdsDoFluxo.has(String(card.etapaId))) return true;
        return card.etapaHistory?.some(h => etapaIdsDoFluxo.has(String(h.etapaId))) || false;
      });
    } else {
      flowCards = cards.filter(card => {
        const cardFlow = flows.find(f => f.etapas?.some(e => String(e.id) === String(card.etapaId)));
        return cardFlow?.id === selectedFlowId;
      });
    }

    flowCards.forEach(card => {
      // Filtro por data de criação
      if (hasDateFilter && tipoFiltroData === 'criacao') {
        const dataCriacao = new Date(card.createdAt).getTime();
        if (dataCriacao < inicioMs || dataCriacao > fimMs) return;
      }

      const history = [...(card.etapaHistory || [])].sort((a, b) =>
        new Date(a.movedAt).getTime() - new Date(b.movedAt).getTime()
      );

      const visited = new Set<string>();
      let prevEtapaId: string | null = null;

      for (let i = 0; i < history.length; i++) {
        const entry = history[i];
        const etapaId = String(entry.etapaId);

        if (!etapaIdsDoFluxo.has(etapaId)) {
          prevEtapaId = etapaId;
          continue;
        }

        const movedAt = new Date(entry.movedAt).getTime();
        const passagemNoPeriodo =
          !hasDateFilter ||
          tipoFiltroData !== 'movimentacao' ||
          (movedAt >= inicioMs && movedAt <= fimMs);

        if (visited.has(etapaId) && passagemNoPeriodo) {
          let stats = result.get(etapaId);
          if (!stats) {
            stats = { passagens: 0, cards: new Set(), vindoDe: new Map() };
            result.set(etapaId, stats);
          }
          stats.passagens++;
          stats.cards.add(card.id);
          if (prevEtapaId) {
            const nomeOrigem = etapaNomeMap.get(prevEtapaId) || 'Outra';
            stats.vindoDe.set(nomeOrigem, (stats.vindoDe.get(nomeOrigem) || 0) + 1);
          }
        }

        visited.add(etapaId);
        prevEtapaId = etapaId;
      }
    });

    return result;
  }, [selectedFlowId, flows, cards, dataInicio, dataFim, tipoFiltroData]);

  // Calcular tempo médio por etapa para o fluxo selecionado
  const etapaAnalytics = useMemo(() => {
    if (!selectedFlowId) return [];

    const selectedFlow = flows.find(f => f.id === selectedFlowId);
    if (!selectedFlow) return [];

    const etapaStats = (selectedFlow.etapas?.filter(etapa => etapasSLA.has(etapa.id)) || []).map(etapa => {
      // Cards/passagens nesta etapa (filtrados por departamento se selecionado)
      const cardsNaEtapa = filteredCardsSLAInfo.filter(info => info.etapaId === etapa.id);
      
      // Calcular tempos
      let tempoTotal = 0;
      let totalComTempo = 0;

      cardsNaEtapa.forEach(info => {
        if (info.horasNaEtapa > 0) {
          tempoTotal += info.horasNaEtapa;
          totalComTempo++;
        }
      });

      // Calcular média
      const tempoMedioHoras = totalComTempo > 0 ? tempoTotal / totalComTempo : 0;
      const tempoMedioDias = tempoMedioHoras / 24;

      // Contar SLAs
      const noPrazo = cardsNaEtapa.filter(c => c.status === 'noPrazo').length;
      const atencao = cardsNaEtapa.filter(c => c.status === 'atencao').length;
      const atrasados = cardsNaEtapa.filter(c => c.status === 'atrasado').length;
      const semSLA = cardsNaEtapa.filter(c => c.status === 'semSLA').length;

      // Reprocessos (cards que voltaram a esta etapa)
      const reprocStats = reprocessosPorEtapa.get(String(etapa.id));
      const reprocessos = reprocStats?.passagens || 0;
      const cardsComReprocesso = reprocStats?.cards.size || 0;
      const reprocessosVindoDe = reprocStats
        ? Array.from(reprocStats.vindoDe.entries())
            .sort((a, b) => b[1] - a[1])
            .map(([nome, count]) => `${nome}: ${count}`)
            .join('\n')
        : '';

      const totalComSLAEtapa = noPrazo + atencao + atrasados;
      const eficiencia = totalComSLAEtapa > 0
        ? Math.round(((noPrazo + atencao) / totalComSLAEtapa) * 100)
        : -1; // -1 para etapas sem SLA ficarem no fim ao ordenar desc

      return {
        id: etapa.id,
        nome: etapa.nome,
        totalCards: cardsNaEtapa.length,
        tempoMedioDias: Math.round(tempoMedioDias * 10) / 10,
        tempoMedioHoras: Math.round(tempoMedioHoras * 10) / 10,
        slaMaximoHoras: etapa.tempoMaximoHoras || null,
        noPrazo,
        atencao,
        atrasados,
        semSLA,
        reprocessos,
        cardsComReprocesso,
        reprocessosVindoDe,
        eficiencia
      };
    });

    return etapaStats;
  }, [selectedFlowId, flows, filteredCardsSLAInfo, etapasSLA, reprocessosPorEtapa]);

  // Análise por colaborador - atribui cards ao responsável real com fallbacks:
  // 1. responsavelIndividualId (atribuído explicitamente ao card)
  // 2. responsavelId do histórico (quem era responsável quando entrou na etapa)
  // 3. Único participante da etapa (se só tem 1 pessoa, é ela)
  // Cards sem nenhum responsável identificável aparecem apenas na visão de etapa/setor
  const colaboradorAnalytics = useMemo(() => {
    if (!selectedFlowId) return [];

    const selectedFlow = flows.find(f => f.id === selectedFlowId);
    if (!selectedFlow) return [];

    // Cards do fluxo (incluindo cards que passaram pelo fluxo)
    const etapaIdsDoFluxo = new Set(selectedFlow.etapas?.map(e => e.id) || []);
    const hasDateFilter = !!(dataInicio || dataFim);

    let flowCards: Card[];
    if (hasDateFilter) {
      flowCards = cards.filter(card => {
        if (etapaIdsDoFluxo.has(card.etapaId)) return true;
        return card.etapaHistory?.some(h => etapaIdsDoFluxo.has(h.etapaId)) || false;
      });
    } else {
      flowCards = cards.filter(card => {
        const cardFlow = flows.find(f => f.etapas?.some(e => e.id === card.etapaId));
        return cardFlow?.id === selectedFlowId;
      });
    }

    // Aplicar filtro de data por criação (se tipo for criação)
    if (hasDateFilter && tipoFiltroData === 'criacao') {
      const inicio = dataInicio ? new Date(dataInicio) : null;
      const fim = dataFim ? new Date(dataFim + 'T23:59:59') : null;
      flowCards = flowCards.filter(card => {
        const dataCriacao = new Date(card.createdAt);
        if (inicio && dataCriacao < inicio) return false;
        if (fim && dataCriacao > fim) return false;
        return true;
      });
    }

    // Mapear etapa -> participantes
    const etapaParticipantes = new Map<string, Set<string>>();
    selectedFlow.etapas?.forEach(etapa => {
      const participantes = new Set<string>();
      etapa.participantes?.forEach(p => {
        if (p) participantes.add(String(p));
      });
      if (etapa.responsaveis) {
        etapa.responsaveis.forEach(r => {
          if (r) participantes.add(String(r));
        });
      }
      etapaParticipantes.set(String(etapa.id), participantes);
    });

    // Coletar todos os colaboradores que participam do fluxo
    const colaboradoresDoFluxo = new Set<string>();
    etapaParticipantes.forEach(participantes => {
      participantes.forEach(p => colaboradoresDoFluxo.add(p));
    });

    // Inicializar estatísticas para TODOS os colaboradores do fluxo
    const colaboradorStats = new Map<string, {
      colaborador: Colaborador;
      totalCards: number;      // Total de cards DISTINTOS que passaram pelas etapas dele
      cardsAtuais: number;     // Cards atualmente nas etapas dele
      noPrazo: number;
      atencao: number;
      atrasados: number;
      semSLA: number;
      tempoTotalHoras: number;
      passagens: number;       // Total de passagens (um card pode passar várias vezes)
      etapasParticipando: number;
    }>();

    // Adicionar todos os colaboradores do fluxo primeiro
    colaboradoresDoFluxo.forEach(colId => {
      const colaborador = colaboradoresMap.get(colId);
      if (colaborador) {
        // Filtrar por departamento se selecionado
        if (selectedDepartamentoId && colaborador.departamentoId !== selectedDepartamentoId) {
          return;
        }
        
        // Contar em quantas etapas participa
        let etapasParticipando = 0;
        etapaParticipantes.forEach((participantes) => {
          if (participantes.has(colId)) {
            etapasParticipando++;
          }
        });

        colaboradorStats.set(colId, {
          colaborador,
          totalCards: 0,
          cardsAtuais: 0,
          noPrazo: 0,
          atencao: 0,
          atrasados: 0,
          semSLA: 0,
          tempoTotalHoras: 0,
          passagens: 0,
          etapasParticipando
        });
      }
    });

    // Processar TODOS os cards e seus históricos de etapas
    flowCards.forEach(card => {
      // Rastrear quais colaboradores já viram este card (para não duplicar totalCards)
      const colaboradoresQueViramEsteCard = new Set<string>();
      
      // Processar histórico de etapas
      const history = [...(card.etapaHistory || [])].sort((a, b) => 
        new Date(a.movedAt).getTime() - new Date(b.movedAt).getTime()
      );

      // Processar cada entrada do histórico
      history.forEach((entry, index) => {
        const etapaId = String(entry.etapaId);
        const participantesDaEtapa = etapaParticipantes.get(etapaId);
        if (!participantesDaEtapa || participantesDaEtapa.size === 0) return;

        // Calcular tempo nesta etapa
        const entradaTime = new Date(entry.movedAt).getTime();
        let saidaTime: number;

        if (entry.leftAt) {
          saidaTime = new Date(entry.leftAt).getTime();
        } else if (index < history.length - 1) {
          saidaTime = new Date(history[index + 1].movedAt).getTime();
        } else {
          saidaTime = new Date().getTime();
        }

        // Para filtro por movimentação: verificar se a passagem sobrepõe o período
        if (hasDateFilter && tipoFiltroData === 'movimentacao') {
          const inicioMs = dataInicio ? new Date(dataInicio).getTime() : 0;
          const fimMs = dataFim ? new Date(dataFim + 'T23:59:59').getTime() : Infinity;
          if (saidaTime < inicioMs || entradaTime > fimMs) return;
        }

        const etapa = selectedFlow.etapas?.find(e => String(e.id) === etapaId);
        const horasNaEtapa = calcularHorasSLA(new Date(entradaTime), new Date(saidaTime), etapa?.slaIgnorarFimDeSemana);
        
        // Verificar SLA para esta passagem
        let statusSLA: 'noPrazo' | 'atencao' | 'atrasado' | 'semSLA' = 'semSLA';
        
        if (etapa?.tempoMaximoHoras && horasNaEtapa > 0) {
          const percentualSLA = (horasNaEtapa / etapa.tempoMaximoHoras) * 100;
          if (horasNaEtapa > etapa.tempoMaximoHoras) {
            statusSLA = 'atrasado';
          } else if (percentualSLA > 80) {
            statusSLA = 'atencao';
          } else {
            statusSLA = 'noPrazo';
          }
        }

        // Determinar o responsável real do card nesta etapa (com fallbacks)
        // 1. responsavelIndividualId do card (atribuído explicitamente)
        // 2. responsavelId do histórico da etapa (quem era responsável quando entrou)
        // 3. Se a etapa tem apenas 1 participante, é ele
        // 4. Se nenhum dos acima, não conta para colaborador (só etapa/setor)
        let responsavelReal: string | null = null;

        if (card.responsavelIndividualId && participantesDaEtapa.has(String(card.responsavelIndividualId))) {
          responsavelReal = String(card.responsavelIndividualId);
        } else if (entry.responsavelId && participantesDaEtapa.has(String(entry.responsavelId))) {
          responsavelReal = String(entry.responsavelId);
        } else if (participantesDaEtapa.size === 1) {
          responsavelReal = Array.from(participantesDaEtapa)[0];
        }

        if (!responsavelReal) return;

        const stats = colaboradorStats.get(responsavelReal);
        if (!stats) return;

        // Contar passagem
        stats.passagens++;
        stats.tempoTotalHoras += horasNaEtapa;

        // Contar SLA
        switch (statusSLA) {
          case 'noPrazo': stats.noPrazo++; break;
          case 'atencao': stats.atencao++; break;
          case 'atrasado': stats.atrasados++; break;
          case 'semSLA': stats.semSLA++; break;
        }

        // Contar card único (apenas uma vez por colaborador)
        if (!colaboradoresQueViramEsteCard.has(responsavelReal)) {
          colaboradoresQueViramEsteCard.add(responsavelReal);
          stats.totalCards++;
        }
      });

      // Contar cards atuais (etapa atual) - com mesmos fallbacks de responsável
      const etapaAtualId = String(card.etapaId);
      const participantesEtapaAtual = etapaParticipantes.get(etapaAtualId);

      if (participantesEtapaAtual) {
        let responsavelAtual: string | null = null;
        if (card.responsavelIndividualId && participantesEtapaAtual.has(String(card.responsavelIndividualId))) {
          responsavelAtual = String(card.responsavelIndividualId);
        } else if (participantesEtapaAtual.size === 1) {
          responsavelAtual = Array.from(participantesEtapaAtual)[0];
        }
        if (responsavelAtual) {
          const stats = colaboradorStats.get(responsavelAtual);
          if (stats) stats.cardsAtuais++;
        }
      }
    });

    // Converter para array e calcular tempo médio
    return Array.from(colaboradorStats.values()).map(stats => {
      const tempoMedioHoras = stats.passagens > 0 ? stats.tempoTotalHoras / stats.passagens : 0;
      const eficiencia = stats.passagens > 0
        ? Math.round(((stats.noPrazo + stats.atencao) / stats.passagens) * 100)
        : -1;
      return {
        ...stats,
        tempoMedioHoras,
        tempoMedioDias: Math.round((tempoMedioHoras / 24) * 10) / 10,
        eficiencia
      };
    });
  }, [selectedFlowId, flows, cards, colaboradoresMap, selectedDepartamentoId, dataInicio, dataFim, tipoFiltroData]);

  // Dados detalhados para drill-down de colaborador
  const drillDownData = useMemo(() => {
    if (!drillDown || !selectedFlowId) return null;
    
    const selectedFlow = flows.find(f => f.id === selectedFlowId);
    if (!selectedFlow) return null;

    // Cards do fluxo
    const flowCards = cards.filter(card => {
      const cardFlow = flows.find(f => f.etapas?.some(e => e.id === card.etapaId));
      return cardFlow?.id === selectedFlowId;
    });

    // Mapear etapa -> participantes
    const etapaParticipantes = new Map<string, Set<string>>();
    selectedFlow.etapas?.forEach(etapa => {
      const participantes = new Set<string>();
      etapa.participantes?.forEach(p => {
        if (p) participantes.add(String(p));
      });
      if (etapa.responsaveis) {
        etapa.responsaveis.forEach(r => {
          if (r) participantes.add(String(r));
        });
      }
      etapaParticipantes.set(String(etapa.id), participantes);
    });

    // Agrupar cards por etapa
    const cardsPorEtapa = new Map<string, {
      etapaNome: string;
      cards: {
        card: Card;
        horasNaEtapa: number;
        status: 'noPrazo' | 'atencao' | 'atrasado' | 'semSLA';
      }[];
    }>();

    // Processar todos os cards
    flowCards.forEach(card => {
      const history = [...(card.etapaHistory || [])].sort((a, b) => 
        new Date(a.movedAt).getTime() - new Date(b.movedAt).getTime()
      );

      history.forEach((entry, index) => {
        const etapaId = String(entry.etapaId);
        const participantesDaEtapa = etapaParticipantes.get(etapaId);
        if (!participantesDaEtapa) return;

        // Determinar responsável real com fallbacks (mesma lógica do analytics)
        let responsavelReal: string | null = null;
        if (card.responsavelIndividualId && participantesDaEtapa.has(String(card.responsavelIndividualId))) {
          responsavelReal = String(card.responsavelIndividualId);
        } else if (entry.responsavelId && participantesDaEtapa.has(String(entry.responsavelId))) {
          responsavelReal = String(entry.responsavelId);
        } else if (participantesDaEtapa.size === 1) {
          responsavelReal = Array.from(participantesDaEtapa)[0];
        }

        if (responsavelReal !== drillDown.colaboradorId) return;

        // Calcular tempo
        const entradaTime = new Date(entry.movedAt).getTime();
        let saidaTime: number;

        if (index < history.length - 1) {
          saidaTime = new Date(history[index + 1].movedAt).getTime();
        } else {
          saidaTime = new Date().getTime();
        }

        const etapa = selectedFlow.etapas?.find(e => String(e.id) === etapaId);
        const horasNaEtapa = calcularHorasSLA(new Date(entradaTime), new Date(saidaTime), etapa?.slaIgnorarFimDeSemana);
        
        // Verificar SLA
        let status: 'noPrazo' | 'atencao' | 'atrasado' | 'semSLA' = 'semSLA';
        
        if (etapa?.tempoMaximoHoras && horasNaEtapa > 0) {
          const percentualSLA = (horasNaEtapa / etapa.tempoMaximoHoras) * 100;
          if (horasNaEtapa > etapa.tempoMaximoHoras) {
            status = 'atrasado';
          } else if (percentualSLA > 80) {
            status = 'atencao';
          } else {
            status = 'noPrazo';
          }
        }

        // Filtrar pelo tipo selecionado no drill-down
        if (drillDown.tipo === 'passagens' || drillDown.tipo === status) {
          if (!cardsPorEtapa.has(etapaId)) {
            cardsPorEtapa.set(etapaId, {
              etapaNome: etapa?.nome || 'Desconhecida',
              cards: []
            });
          }
          
          cardsPorEtapa.get(etapaId)!.cards.push({
            card,
            horasNaEtapa,
            status
          });
        }
      });
    });

    // Converter para array
    return Array.from(cardsPorEtapa.entries()).map(([etapaId, data]) => ({
      etapaId,
      etapaNome: data.etapaNome,
      totalCards: data.cards.length,
      cards: data.cards
    })).sort((a, b) => b.totalCards - a.totalCards);
  }, [drillDown, selectedFlowId, flows, cards]);

  // Dados ordenados para tabela
  const sortedEtapaAnalytics = useMemo(() => {
    const sorted = [...etapaAnalytics];
    sorted.sort((a, b) => {
      const aValue = (a as any)[sortConfig.key] ?? 0;
      const bValue = (b as any)[sortConfig.key] ?? 0;
      return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
    });
    return sorted;
  }, [etapaAnalytics, sortConfig]);

  // Dados ordenados de colaboradores
  const sortedColaboradorAnalytics = useMemo(() => {
    const sorted = [...colaboradorAnalytics];
    sorted.sort((a, b) => {
      const aValue = (a as any)[colaboradorSortConfig.key] ?? 0;
      const bValue = (b as any)[colaboradorSortConfig.key] ?? 0;
      return colaboradorSortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
    });
    return sorted;
  }, [colaboradorAnalytics, colaboradorSortConfig]);

  // Dados para gráfico de barras horizontais (distribuição de cards por etapa)
  const distribuicaoData = useMemo(() => {
    return [...etapaAnalytics]
      .sort((a, b) => b.totalCards - a.totalCards)
      .map((etapa, index) => ({
        nome: etapa.nome.length > 25 ? etapa.nome.substring(0, 25) + '...' : etapa.nome,
        nomeCompleto: etapa.nome,
        cartoes: etapa.totalCards,
        color: COLORS[index % COLORS.length]
      }));
  }, [etapaAnalytics]);

  // Função para ordenar tabela
  const handleSort = (key: string) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'desc' ? 'asc' : 'desc'
    }));
  };

  const handleColaboradorSort = (key: string) => {
    setColaboradorSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'desc' ? 'asc' : 'desc'
    }));
  };

  // Renderizar ícone de ordenação
  const SortIcon = ({ columnKey, config }: { columnKey: string; config: { key: string; direction: 'asc' | 'desc' } }) => {
    if (config.key !== columnKey) return <ChevronDown className="h-3 w-3 text-gray-300 inline" />;
    return config.direction === 'asc' 
      ? <ChevronUp className="h-3 w-3 text-blue-600 inline" />
      : <ChevronDown className="h-3 w-3 text-blue-600 inline" />;
  };

  return (
    <div className="h-full flex flex-col p-6 space-y-6 overflow-y-auto">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-lg">
          <BarChart2 className="h-5 w-5 text-white" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Dashboards</h1>
          <p className="text-sm text-gray-500">Análise de tempo por etapa dos fluxos de trabalho</p>
        </div>
      </div>

      {/* Controles */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200/50 p-5">
        <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-end">
          {/* Seleção de Fluxo */}
          <div className="flex-1 w-full lg:w-auto">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Selecione um Fluxo
            </label>
            <select
              value={selectedFlowId}
              onChange={(e) => {
                setSelectedFlowId(e.target.value);
                setSelectedDepartamentoId('');
              }}
              className="w-full px-4 py-2.5 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all text-gray-900 font-medium"
            >
              <option value="">Escolha um fluxo para análise</option>
              {flows.map(flow => (
                <option key={flow.id} value={flow.id}>{flow.name}</option>
              ))}
            </select>
          </div>

          {/* Filtro por Departamento */}
          {selectedFlowId && (
            <div className="w-full lg:w-64">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                <Building2 className="inline h-4 w-4 mr-1" /> Filtrar por Setor
              </label>
              <select
                value={selectedDepartamentoId}
                onChange={(e) => setSelectedDepartamentoId(e.target.value)}
                className="w-full px-4 py-2.5 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all text-gray-900"
              >
                <option value="">Todos os setores</option>
                {departamentos.map(dep => (
                  <option key={dep.id} value={dep.id}>{dep.nome}</option>
                ))}
              </select>
            </div>
          )}

          {/* Filtro de Etapas para SLA */}
          {selectedFlowId && (
            <div className="relative" ref={filtroEtapasRef}>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                <Filter className="inline h-4 w-4 mr-1" /> Etapas no SLA
              </label>
              <button
                onClick={() => setMostrarFiltroEtapas(!mostrarFiltroEtapas)}
                className="w-full lg:w-64 px-4 py-2.5 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all text-gray-900 text-left flex items-center justify-between"
              >
                <span className="truncate">
                  {(() => {
                    const selectedFlow = flows.find(f => f.id === selectedFlowId);
                    const totalEtapas = selectedFlow?.etapas?.length || 0;
                    if (etapasSLA.size === totalEtapas) return 'Todas as etapas';
                    if (etapasSLA.size === 0) return 'Nenhuma etapa';
                    return `${etapasSLA.size} de ${totalEtapas} etapas`;
                  })()}
                </span>
                <ChevronDown className={`h-4 w-4 ml-2 transition-transform ${mostrarFiltroEtapas ? 'rotate-180' : ''}`} />
              </button>

              {mostrarFiltroEtapas && (
                <div className="absolute z-50 mt-1 w-72 bg-white border border-gray-200 rounded-lg shadow-lg max-h-72 overflow-y-auto">
                  {/* Ações rápidas */}
                  <div className="flex gap-2 p-2 border-b border-gray-100 sticky top-0 bg-white">
                    <button
                      onClick={() => {
                        const selectedFlow = flows.find(f => f.id === selectedFlowId);
                        const ids = selectedFlow?.etapas?.map(e => e.id) || [];
                        setEtapasSLA(new Set(ids));
                      }}
                      className="flex-1 text-xs px-2 py-1 text-blue-600 hover:bg-blue-50 rounded font-medium"
                    >
                      Selecionar Todas
                    </button>
                    <button
                      onClick={() => setEtapasSLA(new Set())}
                      className="flex-1 text-xs px-2 py-1 text-gray-600 hover:bg-gray-50 rounded font-medium"
                    >
                      Limpar
                    </button>
                  </div>

                  {/* Lista de etapas */}
                  {flows.find(f => f.id === selectedFlowId)?.etapas?.map(etapa => (
                    <label
                      key={etapa.id}
                      className="flex items-center gap-3 px-3 py-2 hover:bg-gray-50 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={etapasSLA.has(etapa.id)}
                        onChange={() => {
                          const newSet = new Set(etapasSLA);
                          if (newSet.has(etapa.id)) {
                            newSet.delete(etapa.id);
                          } else {
                            newSet.add(etapa.id);
                          }
                          setEtapasSLA(newSet);
                        }}
                        className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-700 truncate">{etapa.nome}</span>
                      {etapa.tempoMaximoHoras && (
                        <span className="ml-auto text-xs text-gray-400 whitespace-nowrap">{etapa.tempoMaximoHoras}h</span>
                      )}
                    </label>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

          {/* Filtro de Período */}
          {selectedFlowId && (
            <div className="flex flex-col lg:flex-row gap-3 items-start lg:items-end flex-wrap mt-4 pt-4 border-t border-gray-100">
              {/* Tipo de filtro */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  <Calendar className="inline h-4 w-4 mr-1" /> Filtrar por
                </label>
                <select
                  value={tipoFiltroData}
                  onChange={(e) => setTipoFiltroData(e.target.value as 'criacao' | 'movimentacao')}
                  className="px-3 py-2.5 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all text-gray-900 text-sm"
                >
                  <option value="criacao">Data de Criação</option>
                  <option value="movimentacao">Data de Movimentação</option>
                </select>
              </div>

              {/* Data início */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">De</label>
                <input
                  type="date"
                  value={dataInicio}
                  onChange={(e) => setDataInicio(e.target.value)}
                  className="px-3 py-2.5 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all text-gray-900 text-sm"
                />
              </div>

              {/* Data fim */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Até</label>
                <input
                  type="date"
                  value={dataFim}
                  onChange={(e) => setDataFim(e.target.value)}
                  className="px-3 py-2.5 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all text-gray-900 text-sm"
                />
              </div>

              {/* Botões rápidos */}
              <div className="flex gap-1 flex-wrap items-end">
                <button
                  onClick={() => {
                    const hoje = new Date().toISOString().split('T')[0];
                    setDataInicio(hoje);
                    setDataFim(hoje);
                  }}
                  className={`px-3 py-2.5 text-xs font-medium rounded-lg border transition-all ${
                    dataInicio === new Date().toISOString().split('T')[0] && dataFim === new Date().toISOString().split('T')[0]
                      ? 'bg-blue-100 border-blue-300 text-blue-700'
                      : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  Hoje
                </button>
                <button
                  onClick={() => {
                    const fim = new Date();
                    const inicio = new Date();
                    inicio.setDate(inicio.getDate() - 7);
                    setDataInicio(inicio.toISOString().split('T')[0]);
                    setDataFim(fim.toISOString().split('T')[0]);
                  }}
                  className="px-3 py-2.5 text-xs font-medium rounded-lg border bg-white border-gray-300 text-gray-600 hover:bg-gray-50 transition-all"
                >
                  7 dias
                </button>
                <button
                  onClick={() => {
                    const fim = new Date();
                    const inicio = new Date();
                    inicio.setDate(inicio.getDate() - 30);
                    setDataInicio(inicio.toISOString().split('T')[0]);
                    setDataFim(fim.toISOString().split('T')[0]);
                  }}
                  className="px-3 py-2.5 text-xs font-medium rounded-lg border bg-white border-gray-300 text-gray-600 hover:bg-gray-50 transition-all"
                >
                  30 dias
                </button>
                <button
                  onClick={() => {
                    const hoje = new Date();
                    const inicioMes = new Date(hoje.getFullYear(), hoje.getMonth(), 1);
                    setDataInicio(inicioMes.toISOString().split('T')[0]);
                    setDataFim(hoje.toISOString().split('T')[0]);
                  }}
                  className="px-3 py-2.5 text-xs font-medium rounded-lg border bg-white border-gray-300 text-gray-600 hover:bg-gray-50 transition-all"
                >
                  Este mês
                </button>
                {(dataInicio || dataFim) && (
                  <button
                    onClick={() => {
                      setDataInicio('');
                      setDataFim('');
                    }}
                    className="px-3 py-2.5 text-xs font-medium rounded-lg border bg-red-50 border-red-200 text-red-600 hover:bg-red-100 transition-all"
                  >
                    Limpar
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Visões Salvas */}
          {(savedViews.length > 0 || selectedFlowId) && (
            <div className="flex items-center gap-2 mt-4 pt-4 border-t border-gray-100 flex-wrap">
              <Bookmark className="h-4 w-4 text-gray-400 flex-shrink-0" />
              <span className="text-xs font-medium text-gray-500 flex-shrink-0">Visões salvas:</span>
              
              {savedViews.map(view => {
                const flowName = flows.find(f => f.id === view.flowId)?.nome || 'Fluxo removido';
                const isActive = activeViewId === view._id;
                const alterada = visaoAlterada(view);

                // Modo renomear: input inline no lugar do chip
                if (editingViewId === view._id) {
                  return (
                    <div key={view._id} className="flex items-center gap-1">
                      <input
                        ref={renameInputRef}
                        type="text"
                        value={editingName}
                        onChange={(e) => setEditingName(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') renomearVisao(view);
                          if (e.key === 'Escape') { setEditingViewId(null); setEditingName(''); }
                        }}
                        className="px-2 py-1 text-xs border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 w-40"
                        maxLength={30}
                      />
                      <button
                        onClick={() => renomearVisao(view)}
                        disabled={!editingName.trim()}
                        className="px-2 py-1 text-xs font-medium rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                      >
                        Renomear
                      </button>
                      <button
                        onClick={() => { setEditingViewId(null); setEditingName(''); }}
                        className="px-2 py-1 text-xs font-medium rounded-lg text-gray-500 hover:bg-gray-100 transition-all"
                      >
                        Cancelar
                      </button>
                    </div>
                  );
                }

                return (
                  <div key={view._id} className="flex items-center relative">
                    <button
                      onClick={() => carregarVisao(view)}
                      className={`px-3 py-1.5 text-xs font-medium rounded-l-lg border transition-all ${
                        isActive
                          ? 'bg-blue-100 border-blue-300 text-blue-700'
                          : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50 hover:border-gray-300'
                      }`}
                      title={`${flowName}${view.periodoRelativo ? ` • ${view.periodoRelativo}` : view.dataInicio ? ` • ${view.dataInicio} a ${view.dataFim}` : ''}${alterada ? ' • filtros alterados (não salvos)' : ''}`}
                    >
                      {view.nome}
                      {alterada && (
                        <span className="ml-1.5 inline-block h-1.5 w-1.5 rounded-full bg-amber-500 align-middle" title="Filtros alterados" />
                      )}
                    </button>
                    <button
                      onClick={() => setMenuViewId(menuViewId === view._id ? null : view._id)}
                      className={`px-1.5 py-1.5 text-xs rounded-r-lg border border-l-0 transition-all ${
                        isActive
                          ? 'bg-blue-50 border-blue-300 text-blue-500 hover:bg-blue-100'
                          : 'bg-white border-gray-200 text-gray-400 hover:text-gray-700 hover:bg-gray-50'
                      }`}
                      title="Opções da visão"
                    >
                      <MoreVertical className="h-3 w-3" />
                    </button>

                    {menuViewId === view._id && (
                      <div
                        ref={menuVisaoRef}
                        className="absolute top-full left-0 mt-1 z-30 w-56 bg-white border border-gray-200 rounded-lg shadow-lg py-1"
                      >
                        <button
                          onClick={() => atualizarFiltrosVisao(view)}
                          className="w-full text-left px-3 py-2 text-xs text-gray-700 hover:bg-blue-50 flex items-center gap-2"
                        >
                          <Save className="h-3.5 w-3.5 text-blue-500" />
                          <span className="flex-1">Atualizar com filtros atuais</span>
                          {alterada && <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />}
                        </button>
                        <button
                          onClick={() => { setEditingViewId(view._id); setEditingName(view.nome); setMenuViewId(null); }}
                          className="w-full text-left px-3 py-2 text-xs text-gray-700 hover:bg-blue-50 flex items-center gap-2"
                        >
                          <Pencil className="h-3.5 w-3.5 text-gray-400" />
                          Renomear
                        </button>
                        <div className="my-1 border-t border-gray-100" />
                        <button
                          onClick={() => excluirVisao(view._id)}
                          className="w-full text-left px-3 py-2 text-xs text-red-600 hover:bg-red-50 flex items-center gap-2"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                          Excluir
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}

              {selectedFlowId && !showSaveDialog && (
                <button
                  onClick={() => setShowSaveDialog(true)}
                  className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-lg border border-dashed border-gray-300 text-gray-500 hover:border-blue-400 hover:text-blue-600 hover:bg-blue-50 transition-all"
                >
                  <Save className="h-3 w-3" />
                  Salvar visão
                </button>
              )}

              {showSaveDialog && (
                <div className="flex items-center gap-1">
                  <input
                    ref={saveInputRef}
                    type="text"
                    value={newViewName}
                    onChange={(e) => setNewViewName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') salvarVisao();
                      if (e.key === 'Escape') { setShowSaveDialog(false); setNewViewName(''); }
                    }}
                    placeholder="Nome da visão..."
                    className="px-2 py-1 text-xs border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 w-40"
                    maxLength={30}
                  />
                  <button
                    onClick={salvarVisao}
                    disabled={!newViewName.trim()}
                    className="px-2 py-1 text-xs font-medium rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                  >
                    Salvar
                  </button>
                  <button
                    onClick={() => { setShowSaveDialog(false); setNewViewName(''); }}
                    className="px-2 py-1 text-xs font-medium rounded-lg text-gray-500 hover:bg-gray-100 transition-all"
                  >
                    Cancelar
                  </button>
                </div>
              )}

              {savedViews.length === 0 && !selectedFlowId && (
                <span className="text-xs text-gray-400 italic">Nenhuma visão salva</span>
              )}
            </div>
          )}

          {/* Toggle de Visualização */}
          {selectedFlowId && (
            <div className="flex gap-1 bg-gray-100 p-1 rounded-lg mt-4">
              <button
                onClick={() => setViewMode('graficos')}
                className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${
                  viewMode === 'graficos' 
                    ? 'bg-white shadow text-blue-600' 
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <BarChart2 className="h-4 w-4" />
                Gráficos
              </button>
              <button
                onClick={() => setViewMode('tabela')}
                className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${
                  viewMode === 'tabela' 
                    ? 'bg-white shadow text-blue-600' 
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <Table2 className="h-4 w-4" />
                Tabela
              </button>
              <button
                onClick={() => setViewMode('colaboradores')}
                className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${
                  viewMode === 'colaboradores' 
                    ? 'bg-white shadow text-blue-600' 
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <Users className="h-4 w-4" />
                Colaboradores
              </button>
              <button
                onClick={() => setViewMode('segmentacao')}
                className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${
                  viewMode === 'segmentacao'
                    ? 'bg-white shadow text-blue-600'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <Layers className="h-4 w-4" />
                Segmentação
              </button>
              <button
                onClick={() => setViewMode('apresentacao')}
                className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${
                  viewMode === 'apresentacao'
                    ? 'bg-white shadow text-indigo-600'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <Presentation className="h-4 w-4" />
                Apresentação
              </button>
            </div>
          )}
      </div>

      {/* Conteúdo */}
      {!selectedFlowId ? (
        <div className="flex-1 flex items-center justify-center bg-white rounded-xl shadow-sm border border-gray-200/50">
          <div className="text-center space-y-4 py-16">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center mx-auto">
              <BarChart2 className="h-8 w-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900">Selecione um fluxo</h3>
            <p className="text-sm text-gray-500 max-w-sm">
              Escolha um fluxo acima para visualizar a análise de tempo por etapa e SLA
            </p>
          </div>
        </div>
      ) : (
        <>
          {/* Cards de Métricas de SLA (a Apresentação tem os próprios números) */}
          <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 ${viewMode === 'apresentacao' ? 'hidden' : ''}`}>
            {/* No Prazo */}
            <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-5 border border-green-200">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-lg bg-green-500 flex items-center justify-center">
                  <CheckCircle2 className="h-5 w-5 text-white" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-green-700">No Prazo</p>
                  <p className="text-2xl font-bold text-green-900">{slaStats.noPrazo}</p>
                </div>
              </div>
              <p className="text-xs text-green-600">
                {slaStats.total > 0 ? Math.round((slaStats.noPrazo / slaStats.total) * 100) : 0}% do total
              </p>
            </div>

            {/* Atenção */}
            <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-xl p-5 border border-orange-200">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-lg bg-orange-500 flex items-center justify-center">
                  <Clock className="h-5 w-5 text-white" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-orange-700">Atenção (&gt;80%)</p>
                  <p className="text-2xl font-bold text-orange-900">{slaStats.atencao}</p>
                </div>
              </div>
              <p className="text-xs text-orange-600">
                {slaStats.total > 0 ? Math.round((slaStats.atencao / slaStats.total) * 100) : 0}% do total
              </p>
            </div>

            {/* Atrasados */}
            <div className="bg-gradient-to-br from-red-50 to-rose-50 rounded-xl p-5 border border-red-200">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-lg bg-red-500 flex items-center justify-center">
                  <AlertTriangle className="h-5 w-5 text-white" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-red-700">Atrasados</p>
                  <p className="text-2xl font-bold text-red-900">{slaStats.atrasados}</p>
                </div>
              </div>
              <p className="text-xs text-red-600">
                {slaStats.total > 0 ? Math.round((slaStats.atrasados / slaStats.total) * 100) : 0}% do total
              </p>
            </div>

            {/* Total de Cards */}
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-5 border border-blue-200">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-lg bg-blue-500 flex items-center justify-center">
                  <TrendingUp className="h-5 w-5 text-white" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-blue-700">Total de Cards</p>
                  <p className="text-2xl font-bold text-blue-900">{slaStats.total}</p>
                </div>
              </div>
              <p className="text-xs text-blue-600">
                {slaStats.semSLA} sem SLA configurado
              </p>
            </div>
          </div>

          {/* ===== Escalonamento de SLA ===== */}
          {escalonamentos.length > 0 && viewMode !== 'apresentacao' && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200/50 p-6 space-y-5">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-red-100 flex items-center justify-center">
                  <AlertTriangle className="h-4 w-4 text-red-600" />
                </div>
                <div>
                  <h3 className="text-base font-semibold text-gray-900">Escalonamento de SLA</h3>
                  <p className="text-xs text-gray-500">
                    {escalonamentoStats.cardsAfetados} card(s) passaram de 2x o prazo da etapa
                    {' · '}{escalonamentoStats.totalAvisos} aviso(s) enviado(s)
                  </p>
                </div>
              </div>

              {/* Quantos cards chegaram a cada múltiplo do prazo */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {escalonamentoStats.niveis.map(({ nivel, cards }) => (
                  <div
                    key={nivel}
                    className={`rounded-lg p-4 border ${
                      nivel >= 4 ? 'bg-red-100 border-red-300'
                      : nivel === 3 ? 'bg-red-50 border-red-200'
                      : 'bg-orange-50 border-orange-200'
                    }`}
                  >
                    <p className={`text-xs font-medium ${nivel >= 3 ? 'text-red-700' : 'text-orange-700'}`}>
                      {nivel}x o prazo
                    </p>
                    <p className={`text-2xl font-bold ${nivel >= 3 ? 'text-red-900' : 'text-orange-900'}`}>
                      {cards}
                    </p>
                    <p className={`text-[11px] ${nivel >= 3 ? 'text-red-600' : 'text-orange-600'}`}>
                      {nivel === 2 ? 'dobrou' : nivel === 3 ? 'triplicou' : `${nivel} vezes o prazo`}
                    </p>
                  </div>
                ))}
              </div>

              {escalonamentoStats.semDestinatario > 0 && (
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <strong>{escalonamentoStats.semDestinatario}</strong> escalonamento(s) não tinham ninguém para avisar —
                  card sem responsável e etapa sem participantes. Ninguém foi notificado nesses casos.
                </p>
              )}

              {/* Quem foi avisado e o card seguiu parado */}
              {escalonamentoStats.pessoas.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-gray-800 mb-2">Avisos por pessoa</h4>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left text-xs text-gray-500 border-b border-gray-200">
                          <th className="py-2 pr-4 font-medium">Pessoa</th>
                          <th className="py-2 px-3 font-medium text-center">Avisos</th>
                          <th className="py-2 px-3 font-medium text-center">Cards</th>
                          <th className="py-2 px-3 font-medium text-center">Maior nível</th>
                          <th className="py-2 pl-3 font-medium text-center" title="Avisos após os quais o card continuou parado e escalou de novo">
                            Avisado sem ação
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {escalonamentoStats.pessoas.map((p, i) => (
                          <tr key={i} className="hover:bg-gray-50">
                            <td className="py-2 pr-4 text-gray-900">{p.nome}</td>
                            <td className="py-2 px-3 text-center text-gray-700">{p.avisos}</td>
                            <td className="py-2 px-3 text-center text-gray-700">{p.cards}</td>
                            <td className="py-2 px-3 text-center">
                              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                p.nivelMaximo >= 4 ? 'bg-red-200 text-red-900'
                                : p.nivelMaximo === 3 ? 'bg-red-100 text-red-700'
                                : 'bg-orange-100 text-orange-700'
                              }`}>
                                {p.nivelMaximo}x
                              </span>
                            </td>
                            <td className="py-2 pl-3 text-center">
                              {p.semAcao > 0 ? (
                                <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-red-600 text-white">
                                  {p.semAcao}
                                </span>
                              ) : (
                                <span className="text-xs text-gray-400">—</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <p className="text-[11px] text-gray-500 mt-2">
                    "Avisado sem ação" conta os avisos depois dos quais o card continuou parado e escalou para o
                    nível seguinte.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Visualização em Modo Tabela */}
          {viewMode === 'tabela' && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200/50 p-6 space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-indigo-100 flex items-center justify-center">
                    <Table2 className="h-4 w-4 text-indigo-600" />
                  </div>
                  <h3 className="text-base font-semibold text-gray-900">
                    Análise Detalhada por Etapa
                  </h3>
                </div>
                <button
                  onClick={() => setFullscreenMode('tabela')}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  title="Ver em tela cheia"
                >
                  <Maximize2 className="h-4 w-4 text-gray-600" />
                </button>
              </div>
              <div className="overflow-x-auto max-h-[500px]">
                <table className="w-full border-collapse">
                  <thead className="sticky top-0 bg-gray-50 z-10">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider border-b">
                        Etapa
                      </th>
                      <th 
                        className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider border-b cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort('totalCards')}
                      >
                        <div className="flex items-center justify-center gap-1">
                          Cards <SortIcon columnKey="totalCards" config={sortConfig} />
                        </div>
                      </th>
                      <th 
                        className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider border-b cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort('tempoMedioDias')}
                      >
                        <div className="flex items-center justify-center gap-1">
                          Tempo Médio (dias) <SortIcon columnKey="tempoMedioDias" config={sortConfig} />
                        </div>
                      </th>
                      <th 
                        className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider border-b cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort('tempoMedioHoras')}
                      >
                        <div className="flex items-center justify-center gap-1">
                          Tempo Médio (h) <SortIcon columnKey="tempoMedioHoras" config={sortConfig} />
                        </div>
                      </th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider border-b">
                        SLA Máx (h)
                      </th>
                      <th 
                        className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider border-b cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort('noPrazo')}
                      >
                        <div className="flex items-center justify-center gap-1">
                          <span className="text-green-600">✓ No Prazo</span> <SortIcon columnKey="noPrazo" config={sortConfig} />
                        </div>
                      </th>
                      <th 
                        className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider border-b cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort('atencao')}
                      >
                        <div className="flex items-center justify-center gap-1">
                          <span className="text-orange-600">⚠ Atenção</span> <SortIcon columnKey="atencao" config={sortConfig} />
                        </div>
                      </th>
                      <th 
                        className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider border-b cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort('atrasados')}
                      >
                        <div className="flex items-center justify-center gap-1">
                          <span className="text-red-600">✗ Atrasados</span> <SortIcon columnKey="atrasados" config={sortConfig} />
                        </div>
                      </th>
                      <th
                        className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider border-b cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort('cardsComReprocesso')}
                        title="Cards que voltaram a esta etapa após já terem passado por ela (gargalos / retrabalho)"
                      >
                        <div className="flex items-center justify-center gap-1">
                          <span className="text-purple-600">↩ Reprocessos</span> <SortIcon columnKey="cardsComReprocesso" config={sortConfig} />
                        </div>
                      </th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider border-b">
                        Eficiência
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {sortedEtapaAnalytics.map((etapa, idx) => {
                      const totalComSLA = etapa.noPrazo + etapa.atencao + etapa.atrasados;
                      const eficiencia = totalComSLA > 0 ? Math.round(((etapa.noPrazo + etapa.atencao) / totalComSLA) * 100) : 0;
                      return (
                      <tr key={etapa.id} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                        <td className="px-4 py-3 text-sm font-medium text-gray-900">{etapa.nome}</td>
                        <td className="px-4 py-3 text-sm text-center text-gray-700">{etapa.totalCards}</td>
                        <td className="px-4 py-3 text-sm text-center">
                          <span className={`font-semibold ${etapa.slaMaximoHoras && etapa.tempoMedioDias * 24 > etapa.slaMaximoHoras ? 'text-red-600' : 'text-gray-700'}`}>
                            {etapa.tempoMedioDias}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-center">
                          <span className={`font-semibold ${etapa.slaMaximoHoras && etapa.tempoMedioHoras > etapa.slaMaximoHoras ? 'text-red-600' : 'text-gray-700'}`}>
                            {etapa.tempoMedioHoras}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-center text-gray-500">
                          {etapa.slaMaximoHoras || '-'}
                        </td>
                        <td className="px-4 py-3 text-sm text-center">
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            {etapa.noPrazo}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-center">
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                            {etapa.atencao}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-center">
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                            {etapa.atrasados}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-center">
                          {etapa.cardsComReprocesso > 0 ? (() => {
                            const pctReproc = etapa.totalCards > 0
                              ? Math.round((etapa.cardsComReprocesso / etapa.totalCards) * 1000) / 10
                              : 0;
                            const pingPong = Math.max(0, etapa.reprocessos - etapa.cardsComReprocesso);
                            const pingPongLine = pingPong > 0
                              ? `\n${pingPong} card(s) voltaram mais de uma vez (ping-pong)`
                              : '';
                            return (
                            <span
                              className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800 cursor-help"
                              title={etapa.reprocessosVindoDe
                                ? `${etapa.cardsComReprocesso} card(s) com reprocesso (${pctReproc}% de ${etapa.totalCards})${pingPongLine}\n\nVindo de:\n${etapa.reprocessosVindoDe}`
                                : `${etapa.cardsComReprocesso} card(s) com reprocesso (${pctReproc}% de ${etapa.totalCards})${pingPongLine}`}
                            >
                              ↩ {etapa.cardsComReprocesso} <span className="ml-1 opacity-70">({pctReproc}%)</span>
                            </span>
                          );})() : (
                            <span className="text-gray-400 text-xs">-</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-center">
                          <div className="flex items-center justify-center gap-2">
                            <div className="w-16 h-2 bg-gray-200 rounded-full overflow-hidden">
                              <div 
                                className={`h-full rounded-full ${
                                  eficiencia >= 80 ? 'bg-green-500' : 
                                  eficiencia >= 50 ? 'bg-orange-500' : 'bg-red-500'
                                }`}
                                style={{ width: `${eficiencia}%` }}
                              />
                            </div>
                            <span className={`text-xs font-semibold ${
                              eficiencia >= 80 ? 'text-green-600' : 
                              eficiencia >= 50 ? 'text-orange-600' : 'text-red-600'
                            }`}>
                              {totalComSLA > 0 ? `${eficiencia}%` : '-'}
                            </span>
                          </div>
                        </td>
                      </tr>
                    );
                    })}
                  </tbody>
                  <tfoot className="bg-gray-100">
                    <tr className="font-semibold">
                      <td className="px-4 py-3 text-sm text-gray-900">TOTAL</td>
                      <td className="px-4 py-3 text-sm text-center text-gray-900">{slaStats.total}</td>
                      <td className="px-4 py-3 text-sm text-center text-gray-900">
                        {(sortedEtapaAnalytics.reduce((a, b) => a + b.tempoMedioDias, 0) / (sortedEtapaAnalytics.length || 1)).toFixed(1)}
                      </td>
                      <td className="px-4 py-3 text-sm text-center text-gray-900">
                        {(sortedEtapaAnalytics.reduce((a, b) => a + b.tempoMedioHoras, 0) / (sortedEtapaAnalytics.length || 1)).toFixed(1)}
                      </td>
                      <td className="px-4 py-3 text-sm text-center text-gray-500">-</td>
                      <td className="px-4 py-3 text-sm text-center text-green-700">{slaStats.noPrazo}</td>
                      <td className="px-4 py-3 text-sm text-center text-orange-700">{slaStats.atencao}</td>
                      <td className="px-4 py-3 text-sm text-center text-red-700">{slaStats.atrasados}</td>
                      <td className="px-4 py-3 text-sm text-center text-purple-700">
                        {sortedEtapaAnalytics.reduce((a, b) => a + b.cardsComReprocesso, 0)}
                      </td>
                      <td className="px-4 py-3 text-sm text-center text-gray-900">
                        {(() => {
                          const totalComSLA = slaStats.noPrazo + slaStats.atencao + slaStats.atrasados;
                          const totalBom = slaStats.noPrazo + slaStats.atencao;
                          return totalComSLA > 0 ? `${Math.round((totalBom / totalComSLA) * 100)}%` : '-';
                        })()}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          )}

          {viewMode === 'apresentacao' && (() => {
            const fluxoSel = flows.find(f => f.id === selectedFlowId);
            return fluxoSel ? <ApresentacaoFluxo key={fluxoSel.id} flow={fluxoSel} cards={cards} colaboradores={colaboradores} /> : null;
          })()}

          {viewMode === 'segmentacao' && (() => {
            const fluxoSel = flows.find(f => f.id === selectedFlowId);
            return fluxoSel ? <Segmentacao flow={fluxoSel} cards={cards} colaboradores={colaboradores} /> : null;
          })()}

          {/* Visualização de Colaboradores */}
          {viewMode === 'colaboradores' && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200/50 p-6 space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-purple-100 flex items-center justify-center">
                    <Users className="h-4 w-4 text-purple-600" />
                  </div>
                  <div>
                    <h3 className="text-base font-semibold text-gray-900">
                      Desempenho por Colaborador
                    </h3>
                    <p className="text-xs text-gray-500">
                      SLA e eficiência considerando todo histórico de responsabilidade
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setFullscreenMode('colaboradores')}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  title="Ver em tela cheia"
                >
                  <Maximize2 className="h-4 w-4 text-gray-600" />
                </button>
              </div>
              <div className="overflow-x-auto max-h-[500px]">
                <table className="w-full border-collapse">
                  <thead className="sticky top-0 bg-gray-50 z-10">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider border-b">
                        Colaborador
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider border-b">
                        Departamento
                      </th>
                      <th 
                        className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider border-b cursor-pointer hover:bg-gray-100"
                        onClick={() => handleColaboradorSort('totalCards')}
                      >
                        <div className="flex items-center justify-center gap-1">
                          Cards <SortIcon columnKey="totalCards" config={colaboradorSortConfig} />
                        </div>
                      </th>
                      <th 
                        className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider border-b cursor-pointer hover:bg-gray-100"
                        onClick={() => handleColaboradorSort('tempoMedioDias')}
                      >
                        <div className="flex items-center justify-center gap-1">
                          Tempo Médio (dias) <SortIcon columnKey="tempoMedioDias" config={colaboradorSortConfig} />
                        </div>
                      </th>
                      <th 
                        className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider border-b cursor-pointer hover:bg-gray-100"
                        onClick={() => handleColaboradorSort('tempoMedioHoras')}
                      >
                        <div className="flex items-center justify-center gap-1">
                          Tempo Médio (h) <SortIcon columnKey="tempoMedioHoras" config={colaboradorSortConfig} />
                        </div>
                      </th>
                      <th 
                        className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider border-b cursor-pointer hover:bg-gray-100"
                        onClick={() => handleColaboradorSort('noPrazo')}
                      >
                        <div className="flex items-center justify-center gap-1">
                          <span className="text-green-600">✓ No Prazo</span> <SortIcon columnKey="noPrazo" config={colaboradorSortConfig} />
                        </div>
                      </th>
                      <th 
                        className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider border-b cursor-pointer hover:bg-gray-100"
                        onClick={() => handleColaboradorSort('atencao')}
                      >
                        <div className="flex items-center justify-center gap-1">
                          <span className="text-orange-600">⚠ Atenção</span> <SortIcon columnKey="atencao" config={colaboradorSortConfig} />
                        </div>
                      </th>
                      <th 
                        className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider border-b cursor-pointer hover:bg-gray-100"
                        onClick={() => handleColaboradorSort('atrasados')}
                      >
                        <div className="flex items-center justify-center gap-1">
                          <span className="text-red-600">✗ Atrasados</span> <SortIcon columnKey="atrasados" config={colaboradorSortConfig} />
                        </div>
                      </th>
                      <th
                        className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider border-b cursor-pointer hover:bg-gray-100"
                        onClick={() => handleColaboradorSort('eficiencia')}
                      >
                        <div className="flex items-center justify-center gap-1">
                          Eficiência <SortIcon columnKey="eficiencia" config={colaboradorSortConfig} />
                        </div>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {sortedColaboradorAnalytics.map((item, idx) => {
                      const eficiencia = item.passagens > 0 
                        ? Math.round(((item.noPrazo + item.atencao) / item.passagens) * 100) 
                        : 0;
                      const dep = item.colaborador.departamentoId 
                        ? departamentosMap.get(String(item.colaborador.departamentoId))
                        : undefined;
                      
                      return (
                        <tr key={String(item.colaborador.id)} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white text-xs font-semibold">
                                {item.colaborador.nome?.substring(0, 2).toUpperCase() || '??'}
                              </div>
                              <div>
                                <div className="text-sm font-medium text-gray-900">{item.colaborador.nome}</div>
                                <div className="text-xs text-gray-500">{item.colaborador.email}</div>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600">
                            {dep?.nome || '-'}
                          </td>
                          <td className="px-4 py-3 text-sm text-center text-gray-700">{item.totalCards}</td>
                          <td className="px-4 py-3 text-sm text-center">
                            <span className={`font-semibold text-gray-700`}>
                              {item.tempoMedioDias}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm text-center">
                            <span className={`font-semibold text-gray-700`}>
                              {item.tempoMedioHoras.toFixed(1)}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm text-center">
                            <button
                              onClick={() => item.noPrazo > 0 && setDrillDown({
                                colaboradorId: String(item.colaborador.id),
                                colaboradorNome: item.colaborador.nome || 'Colaborador',
                                tipo: 'noPrazo'
                              })}
                              className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 ${item.noPrazo > 0 ? 'hover:bg-green-200 cursor-pointer' : ''}`}
                              disabled={item.noPrazo === 0}
                            >
                              {item.noPrazo}
                            </button>
                          </td>
                          <td className="px-4 py-3 text-sm text-center">
                            <button
                              onClick={() => item.atencao > 0 && setDrillDown({
                                colaboradorId: String(item.colaborador.id),
                                colaboradorNome: item.colaborador.nome || 'Colaborador',
                                tipo: 'atencao'
                              })}
                              className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800 ${item.atencao > 0 ? 'hover:bg-orange-200 cursor-pointer' : ''}`}
                              disabled={item.atencao === 0}
                            >
                              {item.atencao}
                            </button>
                          </td>
                          <td className="px-4 py-3 text-sm text-center">
                            <button
                              onClick={() => item.atrasados > 0 && setDrillDown({
                                colaboradorId: String(item.colaborador.id),
                                colaboradorNome: item.colaborador.nome || 'Colaborador',
                                tipo: 'atrasados'
                              })}
                              className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 ${item.atrasados > 0 ? 'hover:bg-red-200 cursor-pointer' : ''}`}
                              disabled={item.atrasados === 0}
                            >
                              {item.atrasados}
                            </button>
                          </td>
                          <td className="px-4 py-3 text-sm text-center">
                            <div className="flex items-center justify-center gap-2">
                              <div className="w-16 h-2 bg-gray-200 rounded-full overflow-hidden">
                                <div 
                                  className={`h-full rounded-full ${
                                    eficiencia >= 80 ? 'bg-green-500' : 
                                    eficiencia >= 50 ? 'bg-orange-500' : 'bg-red-500'
                                  }`}
                                  style={{ width: `${eficiencia}%` }}
                                />
                              </div>
                              <span className={`text-xs font-semibold ${
                                eficiencia >= 80 ? 'text-green-600' : 
                                eficiencia >= 50 ? 'text-orange-600' : 'text-red-600'
                              }`}>
                                {eficiencia}%
                              </span>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  {sortedColaboradorAnalytics.length > 0 && (
                    <tfoot className="bg-gray-100">
                      <tr className="font-semibold">
                        <td className="px-4 py-3 text-sm text-gray-900" colSpan={2}>TOTAL ({sortedColaboradorAnalytics.length} colaboradores)</td>
                        <td className="px-4 py-3 text-sm text-center text-gray-900">{sortedColaboradorAnalytics.reduce((a, b) => a + b.totalCards, 0)}</td>
                        <td className="px-4 py-3 text-sm text-center text-gray-900">
                          {(sortedColaboradorAnalytics.reduce((a, b) => a + b.tempoMedioDias, 0) / (sortedColaboradorAnalytics.length || 1)).toFixed(1)}
                        </td>
                        <td className="px-4 py-3 text-sm text-center text-gray-900">
                          {(sortedColaboradorAnalytics.reduce((a, b) => a + b.tempoMedioHoras, 0) / (sortedColaboradorAnalytics.length || 1)).toFixed(1)}
                        </td>
                        <td className="px-4 py-3 text-sm text-center text-green-700">{sortedColaboradorAnalytics.reduce((a, b) => a + b.noPrazo, 0)}</td>
                        <td className="px-4 py-3 text-sm text-center text-orange-700">{sortedColaboradorAnalytics.reduce((a, b) => a + b.atencao, 0)}</td>
                        <td className="px-4 py-3 text-sm text-center text-red-700">{sortedColaboradorAnalytics.reduce((a, b) => a + b.atrasados, 0)}</td>
                        <td className="px-4 py-3 text-sm text-center text-gray-900">
                          {(() => {
                            const totalPassagens = sortedColaboradorAnalytics.reduce((a, b) => a + b.passagens, 0);
                            const totalBom = sortedColaboradorAnalytics.reduce((a, b) => a + b.noPrazo + b.atencao, 0);
                            return totalPassagens > 0 ? `${Math.round((totalBom / totalPassagens) * 100)}%` : '-';
                          })()}
                        </td>
                      </tr>
                    </tfoot>
                  )}
                </table>
                {sortedColaboradorAnalytics.length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    Nenhum colaborador participando deste fluxo
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Visualização em Gráficos */}
          {viewMode === 'graficos' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Gráfico de Pizza - Status SLA */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200/50 p-6 space-y-4 relative">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-purple-100 flex items-center justify-center">
                  <TrendingUp className="h-4 w-4 text-purple-600" />
                </div>
                <h3 className="text-base font-semibold text-gray-900">
                  Status SLA dos Cards
                </h3>
              </div>
              <button
                onClick={() => setFullscreenMode('sla')}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                title="Ver em tela cheia"
              >
                <Maximize2 className="h-4 w-4 text-gray-600" />
              </button>
            </div>
            <div className="h-[350px] flex items-center justify-center">
              {slaPieData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={slaPieData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                      outerRadius={100}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {slaPieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value: any) => [`${value} cards`, '']} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="text-center text-gray-500 text-sm">
                  Nenhum dado de SLA disponível
                </div>
              )}
            </div>
          </div>

          {/* Gráfico de Barras - Tempo Médio */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200/50 p-6 space-y-4 relative lg:col-span-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center">
                  <Clock className="h-4 w-4 text-blue-600" />
                </div>
                <h3 className="text-base font-semibold text-gray-900">
                  Tempo Médio por Etapa (Dias)
                </h3>
              </div>
              <button
                onClick={() => setFullscreenMode('tempo')}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                title="Ver em tela cheia"
              >
                <Maximize2 className="h-4 w-4 text-gray-600" />
              </button>
            </div>
            <div className="h-[350px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={etapaAnalytics} margin={{ top: 10, right: 10, left: 0, bottom: 80 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                  <XAxis 
                    dataKey="nome" 
                    angle={-45}
                    textAnchor="end"
                    height={80}
                    fontSize={11}
                    stroke="#6B7280"
                  />
                  <YAxis fontSize={11} stroke="#6B7280" />
                  <Tooltip 
                    formatter={(value: any) => [`${value} dias`, 'Tempo Médio']}
                    contentStyle={{ 
                      borderRadius: '8px',
                      border: '1px solid #E5E7EB',
                      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                    }}
                  />
                  <Bar dataKey="tempoMedioDias" fill="#3B82F6" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Gráfico de Barras Horizontais - Distribuição de Cards */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200/50 p-6 space-y-4 relative lg:col-span-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-green-100 flex items-center justify-center">
                  <LayoutGrid className="h-4 w-4 text-green-600" />
                </div>
                <h3 className="text-base font-semibold text-gray-900">
                  Distribuição de Cartões por Etapa
                </h3>
              </div>
              <button
                onClick={() => setFullscreenMode('distribuicao')}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                title="Ver em tela cheia"
              >
                <Maximize2 className="h-4 w-4 text-gray-600" />
              </button>
            </div>
            <div className="h-[350px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart 
                  data={distribuicaoData}
                  layout="vertical"
                  margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" horizontal={true} vertical={false} />
                  <XAxis 
                    type="number" 
                    fontSize={11} 
                    stroke="#6B7280"
                    tickFormatter={(value) => `${value}`}
                  />
                  <YAxis 
                    type="category" 
                    dataKey="nome" 
                    fontSize={11} 
                    stroke="#6B7280"
                    width={120}
                  />
                  <Tooltip 
                    formatter={(value: any, name: string, props: any) => {
                      if (name === 'cartoes') {
                        return [`${value} cartões`, props.payload.nomeCompleto];
                      }
                      return [value, name];
                    }}
                    contentStyle={{ 
                      borderRadius: '8px',
                      border: '1px solid #E5E7EB',
                      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                    }}
                  />
                  <Bar 
                    dataKey="cartoes" 
                    radius={[0, 6, 6, 0]}
                    fill="#10B981"
                  >
                    {distribuicaoData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
          </div>
        )}
        </>
      )}

      {/* Modal Fullscreen */}
      {fullscreenMode && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-6">
          <div className="bg-white rounded-2xl shadow-2xl w-full h-full max-w-[95vw] max-h-[95vh] flex flex-col">
            {/* Header do Modal */}
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
              <div className="flex items-center gap-3">
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                  fullscreenMode === 'tempo' ? 'bg-blue-100' : 
                  fullscreenMode === 'distribuicao' ? 'bg-green-100' :
                  fullscreenMode === 'sla' ? 'bg-purple-100' :
                  fullscreenMode === 'tabela' ? 'bg-indigo-100' :
                  fullscreenMode === 'colaboradores' ? 'bg-purple-100' :
                  'bg-gray-100'
                }`}>
                  {fullscreenMode === 'tempo' ? <Clock className="h-4 w-4 text-blue-600" /> :
                   fullscreenMode === 'distribuicao' ? <LayoutGrid className="h-4 w-4 text-green-600" /> :
                   fullscreenMode === 'sla' ? <TrendingUp className="h-4 w-4 text-purple-600" /> :
                   fullscreenMode === 'tabela' ? <Table2 className="h-4 w-4 text-indigo-600" /> :
                   fullscreenMode === 'colaboradores' ? <Users className="h-4 w-4 text-purple-600" /> :
                   <BarChart2 className="h-4 w-4 text-gray-600" />}
                </div>
                <h3 className="text-lg font-semibold text-gray-900">
                  {fullscreenMode === 'tempo' ? 'Tempo Médio por Etapa (Dias)' :
                   fullscreenMode === 'distribuicao' ? 'Distribuição de Cartões por Etapa' :
                   fullscreenMode === 'sla' ? 'Status SLA dos Cards' :
                   fullscreenMode === 'tabela' ? 'Análise Detalhada por Etapa' :
                   fullscreenMode === 'colaboradores' ? 'Desempenho por Colaborador' :
                   'Análise'}
                </h3>
              </div>
              <button
                onClick={() => setFullscreenMode(null)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                title="Fechar"
              >
                <X className="h-5 w-5 text-gray-600" />
              </button>
            </div>

            {/* Conteúdo do Modal */}
            <div className="flex-1 p-6 overflow-auto">
              {fullscreenMode === 'sla' && (
                <div className="h-full flex items-center justify-center">
                  {slaPieData.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={slaPieData}
                          cx="50%"
                          cy="50%"
                          labelLine={true}
                          label={({ name, percent, value }) => `${name}: ${value} (${(percent * 100).toFixed(1)}%)`}
                          outerRadius={180}
                          fill="#8884d8"
                          dataKey="value"
                        >
                          {slaPieData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(value: any) => [`${value} cards`, '']} />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="text-center text-gray-500">
                      Nenhum dado de SLA disponível
                    </div>
                  )}
                </div>
              )}

              {fullscreenMode === 'tempo' && (
                <div className="h-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={etapaAnalytics} margin={{ top: 20, right: 30, left: 20, bottom: 100 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                      <XAxis 
                        dataKey="nome" 
                        angle={-45}
                        textAnchor="end"
                        height={100}
                        fontSize={13}
                        stroke="#6B7280"
                      />
                      <YAxis fontSize={13} stroke="#6B7280" />
                      <Tooltip 
                        formatter={(value: any) => [`${value} dias`, 'Tempo Médio']}
                        contentStyle={{ 
                          borderRadius: '8px',
                          border: '1px solid #E5E7EB',
                          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                          fontSize: '14px'
                        }}
                      />
                      <Bar dataKey="tempoMedioDias" fill="#3B82F6" radius={[8, 8, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}

              {fullscreenMode === 'distribuicao' && (
                <div className="h-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart 
                      data={distribuicaoData}
                      layout="vertical"
                      margin={{ top: 20, right: 50, left: 150, bottom: 20 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" horizontal={true} vertical={false} />
                      <XAxis 
                        type="number" 
                        fontSize={13} 
                        stroke="#6B7280"
                        tickFormatter={(value) => `${value}`}
                      />
                      <YAxis 
                        type="category" 
                        dataKey="nomeCompleto"
                        fontSize={13} 
                        stroke="#6B7280"
                        width={140}
                      />
                      <Tooltip 
                        formatter={(value: any, name: string, props: any) => {
                          if (name === 'cartoes') {
                            return [`${value} cartões`, props.payload.nomeCompleto];
                          }
                          return [value, name];
                        }}
                        contentStyle={{ 
                          borderRadius: '8px',
                          border: '1px solid #E5E7EB',
                          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                          fontSize: '14px'
                        }}
                      />
                      <Bar 
                        dataKey="cartoes" 
                        radius={[0, 8, 8, 0]}
                        fill="#10B981"
                      >
                        {distribuicaoData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}

              {fullscreenMode === 'tabela' && (
                <div className="h-full overflow-auto">
                  <table className="w-full border-collapse">
                    <thead className="sticky top-0 bg-gray-50 z-10">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider border-b">Etapa</th>
                        <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider border-b cursor-pointer hover:bg-gray-100" onClick={() => handleSort('totalCards')}>
                          <div className="flex items-center justify-center gap-1">Cards <SortIcon columnKey="totalCards" config={sortConfig} /></div>
                        </th>
                        <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider border-b cursor-pointer hover:bg-gray-100" onClick={() => handleSort('tempoMedioDias')}>
                          <div className="flex items-center justify-center gap-1">Tempo Médio (dias) <SortIcon columnKey="tempoMedioDias" config={sortConfig} /></div>
                        </th>
                        <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider border-b cursor-pointer hover:bg-gray-100" onClick={() => handleSort('tempoMedioHoras')}>
                          <div className="flex items-center justify-center gap-1">Tempo Médio (h) <SortIcon columnKey="tempoMedioHoras" config={sortConfig} /></div>
                        </th>
                        <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider border-b">SLA Máx (h)</th>
                        <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider border-b cursor-pointer hover:bg-gray-100 text-green-600" onClick={() => handleSort('noPrazo')}>
                          <div className="flex items-center justify-center gap-1">✓ No Prazo <SortIcon columnKey="noPrazo" config={sortConfig} /></div>
                        </th>
                        <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider border-b cursor-pointer hover:bg-gray-100 text-orange-600" onClick={() => handleSort('atencao')}>
                          <div className="flex items-center justify-center gap-1">⚠ Atenção <SortIcon columnKey="atencao" config={sortConfig} /></div>
                        </th>
                        <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider border-b cursor-pointer hover:bg-gray-100 text-red-600" onClick={() => handleSort('atrasados')}>
                          <div className="flex items-center justify-center gap-1">✗ Atrasados <SortIcon columnKey="atrasados" config={sortConfig} /></div>
                        </th>
                        <th
                          className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider border-b cursor-pointer hover:bg-gray-100 text-purple-600"
                          onClick={() => handleSort('cardsComReprocesso')}
                          title="Cards que voltaram a esta etapa após já terem passado por ela (gargalos / retrabalho)"
                        >
                          <div className="flex items-center justify-center gap-1">↩ Reprocessos <SortIcon columnKey="cardsComReprocesso" config={sortConfig} /></div>
                        </th>
                        <th
                          className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider border-b cursor-pointer hover:bg-gray-100"
                          onClick={() => handleSort('eficiencia')}
                        >
                          <div className="flex items-center justify-center gap-1">Eficiência <SortIcon columnKey="eficiencia" config={sortConfig} /></div>
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {sortedEtapaAnalytics.map((etapa, idx) => {
                        const totalComSLA = etapa.noPrazo + etapa.atencao + etapa.atrasados;
                        const eficiencia = totalComSLA > 0 ? Math.round(((etapa.noPrazo + etapa.atencao) / totalComSLA) * 100) : 0;
                        return (
                        <tr key={etapa.id} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                          <td className="px-4 py-3 text-sm font-medium text-gray-900">{etapa.nome}</td>
                          <td className="px-4 py-3 text-sm text-center text-gray-700">{etapa.totalCards}</td>
                          <td className="px-4 py-3 text-sm text-center"><span className={`font-semibold ${etapa.slaMaximoHoras && etapa.tempoMedioDias * 24 > etapa.slaMaximoHoras ? 'text-red-600' : 'text-gray-700'}`}>{etapa.tempoMedioDias}</span></td>
                          <td className="px-4 py-3 text-sm text-center"><span className={`font-semibold ${etapa.slaMaximoHoras && etapa.tempoMedioHoras > etapa.slaMaximoHoras ? 'text-red-600' : 'text-gray-700'}`}>{etapa.tempoMedioHoras}</span></td>
                          <td className="px-4 py-3 text-sm text-center text-gray-500">{etapa.slaMaximoHoras || '-'}</td>
                          <td className="px-4 py-3 text-sm text-center"><span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">{etapa.noPrazo}</span></td>
                          <td className="px-4 py-3 text-sm text-center"><span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">{etapa.atencao}</span></td>
                          <td className="px-4 py-3 text-sm text-center"><span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">{etapa.atrasados}</span></td>
                          <td className="px-4 py-3 text-sm text-center">
                            {etapa.cardsComReprocesso > 0 ? (() => {
                              const pctReproc = etapa.totalCards > 0
                                ? Math.round((etapa.cardsComReprocesso / etapa.totalCards) * 1000) / 10
                                : 0;
                              const pingPong = Math.max(0, etapa.reprocessos - etapa.cardsComReprocesso);
                              const pingPongLine = pingPong > 0
                                ? `\n${pingPong} card(s) voltaram mais de uma vez (ping-pong)`
                                : '';
                              return (
                              <span
                                className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800 cursor-help"
                                title={etapa.reprocessosVindoDe
                                  ? `${etapa.cardsComReprocesso} card(s) com reprocesso (${pctReproc}% de ${etapa.totalCards})${pingPongLine}\n\nVindo de:\n${etapa.reprocessosVindoDe}`
                                  : `${etapa.cardsComReprocesso} card(s) com reprocesso (${pctReproc}% de ${etapa.totalCards})${pingPongLine}`}
                              >
                                ↩ {etapa.cardsComReprocesso} <span className="ml-1 opacity-70">({pctReproc}%)</span>
                              </span>
                            );})() : (
                              <span className="text-gray-400 text-xs">-</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-sm text-center">
                            <div className="flex items-center justify-center gap-2">
                              <div className="w-16 h-2 bg-gray-200 rounded-full overflow-hidden">
                                <div className={`h-full rounded-full ${eficiencia >= 80 ? 'bg-green-500' : eficiencia >= 50 ? 'bg-orange-500' : 'bg-red-500'}`} style={{ width: `${eficiencia}%` }} />
                              </div>
                              <span className={`text-xs font-semibold ${eficiencia >= 80 ? 'text-green-600' : eficiencia >= 50 ? 'text-orange-600' : 'text-red-600'}`}>{totalComSLA > 0 ? `${eficiencia}%` : '-'}</span>
                            </div>
                          </td>
                        </tr>
                      );
                      })}
                    </tbody>
                    <tfoot className="bg-gray-100">
                      <tr className="font-semibold">
                        <td className="px-4 py-3 text-sm text-gray-900">TOTAL</td>
                        <td className="px-4 py-3 text-sm text-center text-gray-900">{slaStats.total}</td>
                        <td className="px-4 py-3 text-sm text-center text-gray-900">{(sortedEtapaAnalytics.reduce((a, b) => a + b.tempoMedioDias, 0) / (sortedEtapaAnalytics.length || 1)).toFixed(1)}</td>
                        <td className="px-4 py-3 text-sm text-center text-gray-900">{(sortedEtapaAnalytics.reduce((a, b) => a + b.tempoMedioHoras, 0) / (sortedEtapaAnalytics.length || 1)).toFixed(1)}</td>
                        <td className="px-4 py-3 text-sm text-center text-gray-500">-</td>
                        <td className="px-4 py-3 text-sm text-center text-green-700">{slaStats.noPrazo}</td>
                        <td className="px-4 py-3 text-sm text-center text-orange-700">{slaStats.atencao}</td>
                        <td className="px-4 py-3 text-sm text-center text-red-700">{slaStats.atrasados}</td>
                        <td className="px-4 py-3 text-sm text-center text-purple-700">{sortedEtapaAnalytics.reduce((a, b) => a + b.cardsComReprocesso, 0)}</td>
                        <td className="px-4 py-3 text-sm text-center text-gray-900">
                          {(() => {
                            const totalComSLA = slaStats.noPrazo + slaStats.atencao + slaStats.atrasados;
                            const totalBom = slaStats.noPrazo + slaStats.atencao;
                            return totalComSLA > 0 ? `${Math.round((totalBom / totalComSLA) * 100)}%` : '-';
                          })()}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}

              {fullscreenMode === 'colaboradores' && (
                <div className="h-full overflow-auto">
                  <table className="w-full border-collapse">
                    <thead className="sticky top-0 bg-gray-50 z-10">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider border-b">Colaborador</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider border-b">Departamento</th>
                        <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider border-b cursor-pointer hover:bg-gray-100" onClick={() => handleColaboradorSort('totalCards')}>
                          <div className="flex items-center justify-center gap-1">Cards <SortIcon columnKey="totalCards" config={colaboradorSortConfig} /></div>
                        </th>
                        <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider border-b cursor-pointer hover:bg-gray-100" onClick={() => handleColaboradorSort('tempoMedioDias')}>
                          <div className="flex items-center justify-center gap-1">Tempo Médio (dias) <SortIcon columnKey="tempoMedioDias" config={colaboradorSortConfig} /></div>
                        </th>
                        <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider border-b cursor-pointer hover:bg-gray-100" onClick={() => handleColaboradorSort('tempoMedioHoras')}>
                          <div className="flex items-center justify-center gap-1">Tempo Médio (h) <SortIcon columnKey="tempoMedioHoras" config={colaboradorSortConfig} /></div>
                        </th>
                        <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider border-b cursor-pointer hover:bg-gray-100 text-green-600" onClick={() => handleColaboradorSort('noPrazo')}>
                          <div className="flex items-center justify-center gap-1">✓ No Prazo <SortIcon columnKey="noPrazo" config={colaboradorSortConfig} /></div>
                        </th>
                        <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider border-b cursor-pointer hover:bg-gray-100 text-orange-600" onClick={() => handleColaboradorSort('atencao')}>
                          <div className="flex items-center justify-center gap-1">⚠ Atenção <SortIcon columnKey="atencao" config={colaboradorSortConfig} /></div>
                        </th>
                        <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider border-b cursor-pointer hover:bg-gray-100 text-red-600" onClick={() => handleColaboradorSort('atrasados')}>
                          <div className="flex items-center justify-center gap-1">✗ Atrasados <SortIcon columnKey="atrasados" config={colaboradorSortConfig} /></div>
                        </th>
                        <th
                          className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider border-b cursor-pointer hover:bg-gray-100"
                          onClick={() => handleColaboradorSort('eficiencia')}
                        >
                          <div className="flex items-center justify-center gap-1">Eficiência <SortIcon columnKey="eficiencia" config={colaboradorSortConfig} /></div>
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {sortedColaboradorAnalytics.map((item, idx) => {
                        const eficiencia = item.passagens > 0 ? Math.round(((item.noPrazo + item.atencao) / item.passagens) * 100) : 0;
                        const dep = item.colaborador.departamentoId ? departamentosMap.get(String(item.colaborador.departamentoId)) : undefined;
                        return (
                          <tr key={String(item.colaborador.id)} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-3">
                                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white text-xs font-semibold">
                                  {item.colaborador.nome?.substring(0, 2).toUpperCase() || '??'}
                                </div>
                                <div>
                                  <div className="text-sm font-medium text-gray-900">{item.colaborador.nome}</div>
                                  <div className="text-xs text-gray-500">{item.colaborador.email}</div>
                                </div>
                              </div>
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600">{dep?.nome || '-'}</td>
                            <td className="px-4 py-3 text-sm text-center text-gray-700">{item.totalCards}</td>
                            <td className="px-4 py-3 text-sm text-center font-semibold text-gray-700">{item.tempoMedioDias}</td>
                            <td className="px-4 py-3 text-sm text-center font-semibold text-gray-700">{item.tempoMedioHoras.toFixed(1)}</td>
                            <td className="px-4 py-3 text-sm text-center"><span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">{item.noPrazo}</span></td>
                            <td className="px-4 py-3 text-sm text-center"><span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">{item.atencao}</span></td>
                            <td className="px-4 py-3 text-sm text-center"><span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">{item.atrasados}</span></td>
                            <td className="px-4 py-3 text-sm text-center">
                              <div className="flex items-center justify-center gap-2">
                                <div className="w-16 h-2 bg-gray-200 rounded-full overflow-hidden">
                                  <div className={`h-full rounded-full ${eficiencia >= 80 ? 'bg-green-500' : eficiencia >= 50 ? 'bg-orange-500' : 'bg-red-500'}`} style={{ width: `${eficiencia}%` }} />
                                </div>
                                <span className={`text-xs font-semibold ${eficiencia >= 80 ? 'text-green-600' : eficiencia >= 50 ? 'text-orange-600' : 'text-red-600'}`}>{eficiencia}%</span>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                    {sortedColaboradorAnalytics.length > 0 && (
                      <tfoot className="bg-gray-100">
                        <tr className="font-semibold">
                          <td className="px-4 py-3 text-sm text-gray-900" colSpan={2}>TOTAL ({sortedColaboradorAnalytics.length} colaboradores)</td>
                          <td className="px-4 py-3 text-sm text-center text-gray-900">{sortedColaboradorAnalytics.reduce((a, b) => a + b.totalCards, 0)}</td>
                          <td className="px-4 py-3 text-sm text-center text-gray-900">{(sortedColaboradorAnalytics.reduce((a, b) => a + b.tempoMedioDias, 0) / (sortedColaboradorAnalytics.length || 1)).toFixed(1)}</td>
                          <td className="px-4 py-3 text-sm text-center text-gray-900">{(sortedColaboradorAnalytics.reduce((a, b) => a + b.tempoMedioHoras, 0) / (sortedColaboradorAnalytics.length || 1)).toFixed(1)}</td>
                          <td className="px-4 py-3 text-sm text-center text-green-700">{sortedColaboradorAnalytics.reduce((a, b) => a + b.noPrazo, 0)}</td>
                          <td className="px-4 py-3 text-sm text-center text-orange-700">{sortedColaboradorAnalytics.reduce((a, b) => a + b.atencao, 0)}</td>
                          <td className="px-4 py-3 text-sm text-center text-red-700">{sortedColaboradorAnalytics.reduce((a, b) => a + b.atrasados, 0)}</td>
                          <td className="px-4 py-3 text-sm text-center text-gray-900">
                            {(() => {
                              const totalPassagens = sortedColaboradorAnalytics.reduce((a, b) => a + b.passagens, 0);
                              const totalBom = sortedColaboradorAnalytics.reduce((a, b) => a + b.noPrazo + b.atencao, 0);
                              return totalPassagens > 0 ? `${Math.round((totalBom / totalPassagens) * 100)}%` : '-';
                            })()}
                          </td>
                        </tr>
                      </tfoot>
                    )}
                  </table>
                  {sortedColaboradorAnalytics.length === 0 && (
                    <div className="text-center py-8 text-gray-500">Nenhum colaborador participando deste fluxo</div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal de Drill-Down */}
      {drillDown && drillDownData && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[85vh] flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white rounded-t-xl">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                  drillDown.tipo === 'atrasados' ? 'bg-red-100' :
                  drillDown.tipo === 'atencao' ? 'bg-orange-100' :
                  drillDown.tipo === 'noPrazo' ? 'bg-green-100' : 'bg-indigo-100'
                }`}>
                  <span className="text-lg">
                    {drillDown.tipo === 'atrasados' ? '🔴' :
                     drillDown.tipo === 'atencao' ? '🟡' :
                     drillDown.tipo === 'noPrazo' ? '🟢' : '📊'}
                  </span>
                </div>
                <div>
                  <h2 className="text-lg font-bold text-gray-900">
                    {drillDown.colaboradorNome}
                  </h2>
                  <p className="text-sm text-gray-500">
                    {drillDown.tipo === 'atrasados' ? 'Cards Atrasados' :
                     drillDown.tipo === 'atencao' ? 'Cards em Atenção' :
                     drillDown.tipo === 'noPrazo' ? 'Cards no Prazo' : 'Todas as Passagens'}
                    {' • '}{drillDownData.reduce((a, b) => a + b.totalCards, 0)} registros
                  </p>
                </div>
              </div>
              <button
                onClick={() => setDrillDown(null)}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-4">
              {drillDownData.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <p className="text-lg">Nenhum registro encontrado</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {drillDownData.map(etapaGroup => (
                    <div key={etapaGroup.etapaId} className="border border-gray-200 rounded-lg overflow-hidden">
                      {/* Cabeçalho da Etapa */}
                      <button
                        onClick={() => setDrillDown(prev => prev ? {
                          ...prev,
                          etapaSelecionada: prev.etapaSelecionada === etapaGroup.etapaId ? undefined : etapaGroup.etapaId
                        } : null)}
                        className="w-full flex items-center justify-between p-3 bg-gray-50 hover:bg-gray-100 transition-colors"
                      >
                        <div className="flex items-center gap-2">
                          <LayoutGrid className="h-4 w-4 text-gray-400" />
                          <span className="font-medium text-gray-900">{etapaGroup.etapaNome}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            drillDown.tipo === 'atrasados' ? 'bg-red-100 text-red-700' :
                            drillDown.tipo === 'atencao' ? 'bg-orange-100 text-orange-700' :
                            drillDown.tipo === 'noPrazo' ? 'bg-green-100 text-green-700' : 'bg-indigo-100 text-indigo-700'
                          }`}>
                            {etapaGroup.totalCards} cards
                          </span>
                          {drillDown.etapaSelecionada === etapaGroup.etapaId ? (
                            <ChevronUp className="h-4 w-4 text-gray-400" />
                          ) : (
                            <ChevronDown className="h-4 w-4 text-gray-400" />
                          )}
                        </div>
                      </button>

                      {/* Lista de Cards (expandido) */}
                      {drillDown.etapaSelecionada === etapaGroup.etapaId && (
                        <div className="divide-y divide-gray-100">
                          {etapaGroup.cards.map((item, idx) => (
                            <div key={`${item.card.id}-${idx}`} className="p-3 hover:bg-gray-50 transition-colors">
                              <div className="flex items-center justify-between">
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium text-gray-900 truncate">
                                    {item.card.titulo}
                                  </p>
                                  <div className="flex items-center gap-2 mt-1">
                                    <span className="text-xs text-gray-500">
                                      Tempo: {item.horasNaEtapa.toFixed(1)}h
                                    </span>
                                    <span className={`text-xs px-1.5 py-0.5 rounded ${
                                      item.status === 'atrasado' ? 'bg-red-100 text-red-700' :
                                      item.status === 'atencao' ? 'bg-orange-100 text-orange-700' :
                                      item.status === 'noPrazo' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                                    }`}>
                                      {item.status === 'atrasado' ? 'Atrasado' :
                                       item.status === 'atencao' ? 'Atenção' :
                                       item.status === 'noPrazo' ? 'No prazo' : 'Sem SLA'}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="p-4 border-t border-gray-200 bg-gray-50 rounded-b-xl">
              <div className="flex justify-between items-center">
                <p className="text-sm text-gray-500">
                  Clique em uma etapa para ver os cards
                </p>
                <button
                  onClick={() => setDrillDown(null)}
                  className="px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-700 transition-colors text-sm font-medium"
                >
                  Fechar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
